import React, { useState, useCallback, useRef, useMemo, useImperativeHandle, forwardRef } from 'react';
import ReactFlow, {
    Background,
    Controls,
    MiniMap,
    addEdge,
    useNodesState,
    useEdgesState,
    MarkerType,
    Handle,
    Position,
    BaseEdge,
    EdgeLabelRenderer,
    getBezierPath,
    getSmoothStepPath,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { X, Plus, Trash2, Edit2, Link2, Hash, Cpu, GitBranch, Braces, Square, Sparkles, ClipboardPaste, Copy, LayoutDashboard, PhoneForwarded } from 'lucide-react';
import CustomFunctionModal from './CustomFunctionModal';

// Custom Node Component
const CustomNode = ({ data, selected, id }) => {
    const isEndNode = data.isEndNode || false;
    const isGlobalNode = data.isGlobalNode || false;
    const onDelete = data.onDelete;
    const connectionHighlight = data.connectionHighlight || false;
    
    return (
        <div className={`bg-card rounded-xl shadow-xl shadow-primary/10 border-[3px] transition-all duration-200 ${selected ? (isEndNode ? 'border-destructive' : 'border-primary') : connectionHighlight ? 'border-primary ring-4 ring-primary/30 ring-offset-4' : 'border-border'}`} style={{ minWidth: '700px', maxWidth: '950px' }}>
            <div className="p-8">
                <div className="mb-6 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <h3 style={{ fontSize: '28px', lineHeight: '34px' }} className="font-bold text-foreground"># {data.name || 'Unnamed Node'}</h3>
                        {isGlobalNode && (
                            <span className="bg-primary text-primary-foreground font-bold px-3 py-2 rounded" style={{ fontSize: '16px' }}>GLOBAL</span>
                        )}
                        {isEndNode && (
                            <span className="bg-destructive text-destructive-foreground font-bold px-3 py-2 rounded" style={{ fontSize: '16px' }}>END</span>
                        )}
                    </div>
                    {onDelete && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm(`Are you sure you want to delete "${data.name || 'Unnamed Node'}"?`)) {
                                    onDelete(id);
                                }
                            }}
                            className="text-destructive hover:text-destructive/80 hover:bg-destructive/10 p-2 rounded transition-colors"
                            title="Delete node"
                        >
                            <Trash2 size={24} />
                        </button>
                    )}
                </div>

                <div className="mb-6 space-y-3">
                    {data.prompt ? (
                        <div className="text-foreground whitespace-pre-wrap bg-muted/50 rounded-lg p-6 border border-border" style={{ fontSize: '20px', lineHeight: '30px' }}>
                            <div className="font-semibold mb-3" style={{ fontSize: '20px' }}>Greeting:</div>
                            <div className="leading-relaxed">{data.prompt}</div>
                        </div>
                    ) : (
                        <div className="text-muted-foreground italic bg-muted/30 rounded-lg p-4" style={{ fontSize: '18px' }}>No prompt set</div>
                    )}
                </div>

                <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-3">
                        <span className="font-semibold text-foreground" style={{ fontSize: '22px' }}>Transition</span>
                    </div>
                    
                    {data.transitions && data.transitions.length > 0 ? (
                        data.transitions.map((transition, index) => {
                            return (
                                <div key={transition.id || index} className="flex items-center gap-3 group relative">
                                    <div className="flex-1 text-foreground bg-muted/50 rounded-lg px-5 py-4 border border-border" style={{ fontSize: '20px', lineHeight: '28px' }}>
                                        • {transition.condition || 'No condition'}
                                    </div>
                                    <Handle
                                        type="source"
                                        position={Position.Right}
                                        id={`transition-${index}`}
                                        className="!bg-primary !border-2 !border-card !w-4 !h-4 !right-[-8px]"
                                    />
                                </div>
                            );
                        })
                    ) : (
                        <div className="text-muted-foreground italic" style={{ fontSize: '18px' }}>No transitions</div>
                    )}
                </div>

                {data.variables_to_extract && data.variables_to_extract.length > 0 && (
                    <div className="mt-5 pt-5 border-t border-border">
                        <div className="text-muted-foreground" style={{ fontSize: '16px' }}>
                            Variables: {data.variables_to_extract.join(', ')}
                        </div>
                    </div>
                )}
            </div>
            
            <Handle
                type="target"
                position={Position.Left}
                id="target"
                className="!bg-primary !border-2 !border-card !w-4 !h-4 !left-[-8px]"
            />
        </div>
    );
};

// Custom Function Node Component
const CustomFunctionNode = ({ data, selected, id }) => {
    const customFunction = data.customFunction || {};
    const apiEndpoint = customFunction.api_endpoint || {};
    const onDelete = data.onDelete;
    const connectionHighlight = data.connectionHighlight || false;
    
    return (
        <div className={`bg-card rounded-xl shadow-xl shadow-primary/10 border-[3px] transition-all duration-200 ${selected ? 'border-primary' : connectionHighlight ? 'border-primary ring-4 ring-primary/30 ring-offset-4' : 'border-border'}`} style={{ minWidth: '700px', maxWidth: '950px' }}>
            <div className="p-8">
                <div className="mb-6 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <Link2 size={28} className="text-primary" />
                        <h3 style={{ fontSize: '28px', lineHeight: '34px' }} className="font-bold text-foreground">{data.name || 'Custom Function'}</h3>
                    </div>
                    {onDelete && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm(`Are you sure you want to delete "${data.name || 'Custom Function'}"?`)) {
                                    onDelete(id);
                                }
                            }}
                            className="text-destructive hover:text-destructive/80 hover:bg-destructive/10 p-2 rounded transition-colors"
                            title="Delete node"
                        >
                            <Trash2 size={24} />
                        </button>
                    )}
                </div>

                {customFunction.description && (
                    <div className="mb-5 text-foreground bg-muted/50 rounded-lg p-6 border border-border" style={{ fontSize: '20px', lineHeight: '30px' }}>
                        {customFunction.description}
                    </div>
                )}

                {apiEndpoint.method && apiEndpoint.url && (
                    <div className="mb-5 text-foreground bg-muted/50 rounded-lg p-6 border border-border" style={{ fontSize: '20px', lineHeight: '30px' }}>
                        <div className="font-semibold mb-3" style={{ fontSize: '20px' }}>API:</div>
                        <div className="font-mono">
                            <span className="text-primary font-bold">{apiEndpoint.method}</span> {apiEndpoint.url}
                        </div>
                    </div>
                )}

                {data.prompt && (
                    <div className="mb-5 text-foreground bg-muted/50 rounded-lg p-6 border border-border" style={{ fontSize: '20px', lineHeight: '30px' }}>
                        <div className="font-semibold mb-3" style={{ fontSize: '20px' }}>Message:</div>
                        <div>{data.prompt}</div>
                    </div>
                )}

                <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-3">
                        <span className="font-semibold text-foreground" style={{ fontSize: '22px' }}>Transitions</span>
                    </div>
                    
                    {data.transitions && data.transitions.length > 0 ? (
                        data.transitions.map((transition, index) => (
                            <div key={transition.id || index} className="flex items-center gap-3 group relative">
                                <div className="flex-1 text-foreground bg-muted/50 rounded-lg px-5 py-4 border border-border" style={{ fontSize: '20px', lineHeight: '28px' }}>
                                    • {transition.condition || 'No condition'}
                                </div>
                                <Handle
                                    type="source"
                                    position={Position.Right}
                                    id={`transition-${index}`}
                                    className="!bg-primary !border-2 !border-card !w-4 !h-4 !right-[-8px]"
                                    style={{ top: '50%', transform: 'translateY(-50%)', position: 'absolute' }}
                                />
                            </div>
                        ))
                    ) : (
                        <div className="text-muted-foreground italic" style={{ fontSize: '18px' }}>No transitions</div>
                    )}
                </div>
            </div>
            
            <Handle
                type="target"
                position={Position.Left}
                id="target"
                className="!bg-primary !border-2 !border-card !w-4 !h-4 !left-[-8px]"
            />
        </div>
    );
};

// Logic Split Node Component
const LogicSplitNode = ({ data, selected, id }) => {
    const onDelete = data.onDelete;
    const connectionHighlight = data.connectionHighlight || false;
    const transitions = data.transitions || [];
    const elseTransition = transitions.find(t => t.isElse) || null;
    const regularTransitions = transitions.filter(t => !t.isElse);
    
    return (
        <div className={`bg-gradient-button rounded-xl shadow-xl shadow-primary/20 border-[3px] transition-all duration-200 ${selected ? 'border-primary-foreground' : connectionHighlight ? 'border-primary-foreground ring-4 ring-primary-foreground/40 ring-offset-4' : 'border-primary/50'}`} style={{ minWidth: '700px', maxWidth: '950px' }}>
            <div className="p-8">
                <div className="mb-6 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <GitBranch size={28} className="text-primary-foreground" />
                        <h3 style={{ fontSize: '28px', lineHeight: '34px' }} className="font-bold text-primary-foreground">{data.name || data.label || 'Logic Split Node'}</h3>
                        {data.isGlobalNode && (
                            <span className="bg-primary-foreground text-primary font-bold px-3 py-2 rounded" style={{ fontSize: '16px' }}>GLOBAL</span>
                        )}
                    </div>
                    {onDelete && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm(`Are you sure you want to delete "${data.name || data.label || 'Logic Split Node'}"?`)) {
                                    onDelete(id);
                                }
                            }}
                            className="text-destructive-foreground/80 hover:text-destructive-foreground hover:bg-destructive/20 p-2 rounded transition-colors"
                            title="Delete node"
                        >
                            <Trash2 size={24} />
                        </button>
                    )}
                </div>

                {data.isGlobalNode && (data.globalTriggers || []).length > 0 && (
                    <div className="mb-5 space-y-2">
                        <div className="font-semibold text-primary-foreground" style={{ fontSize: '20px' }}>Triggers when:</div>
                        {data.globalTriggers.map((t, index) => (
                            <div key={t.id || index} className="text-primary-foreground bg-primary/20 rounded-lg px-5 py-3 border border-primary/30" style={{ fontSize: '18px' }}>
                                • {t.condition}
                            </div>
                        ))}
                    </div>
                )}

                <div className="space-y-3">
                    {regularTransitions.length > 0 ? (
                        regularTransitions.map((transition, index) => {
                            return (
                                <div key={transition.id || index} className="flex items-center gap-3 group relative">
                                    <div className="flex-1 text-primary-foreground bg-primary/20 rounded-lg px-5 py-4 border border-primary/30" style={{ fontSize: '20px', lineHeight: '28px' }}>
                                        <GitBranch size={20} className="inline mr-2" />
                                        {transition.condition || 'No condition'}
                                    </div>
                                    <Handle
                                        type="source"
                                        position={Position.Right}
                                        id={`transition-${index}`}
                                        className="!bg-primary-foreground !border-2 !border-primary !w-4 !h-4 !right-[-8px]"
                                    />
                                </div>
                            );
                        })
                    ) : (
                        <div className="text-primary-foreground/60 italic" style={{ fontSize: '18px' }}>No transitions</div>
                    )}
                    
                    {elseTransition && (
                        <div className="flex items-center gap-3 group relative mt-4 pt-4 border-t border-primary/30">
                            <div className="flex-1 text-primary-foreground bg-primary/20 rounded-lg px-5 py-4 border border-primary/30" style={{ fontSize: '20px', lineHeight: '28px' }}>
                                <Sparkles size={20} className="inline mr-2" />
                                Else
                            </div>
                            <Handle
                                type="source"
                                position={Position.Right}
                                id={`transition-else`}
                                className="!bg-primary-foreground !border-2 !border-primary !w-4 !h-4 !right-[-8px]"
                            />
                        </div>
                    )}
                </div>
            </div>
            
            <Handle
                type="target"
                position={Position.Left}
                id="target"
                className="!bg-primary-foreground !border-2 !border-primary !w-4 !h-4 !left-[-8px]"
            />
        </div>
    );
};

// Transfer Call Node defaults (mirrors app/agent/flow_engine.py)
const TRANSFER_RING_DEFAULT_S = 30;
const TRANSFER_RING_MIN_S = 5;
const TRANSFER_RING_MAX_S = 120;
const DEFAULT_TRANSFER_MESSAGES = {
    warm: 'Please hold for a moment while I connect you with someone who can help.',
    cold: "I'm transferring your call now. Please hold.",
};
const DEFAULT_TRANSFER_FAILURE_MESSAGE = "I'm sorry, the person I tried to reach is away right now, so I wasn't able to complete the transfer. I'm still here with you, so let's keep going.";

// The one outgoing route of a transfer node, followed only when the transfer fails.
// Kept first in the transitions list so its handle is always "transition-0".
const makeTransferFailedRoute = () => ({
    id: `transfer-failed-${Date.now()}`,
    type: 'transfer_failed',
    condition: 'Transfer failed',
    target_node_id: ''
});

const normalizeTransferTransitions = (transitions = []) => {
    const failed = transitions.find(t => t.type === 'transfer_failed') || makeTransferFailedRoute();
    return [failed, ...transitions.filter(t => t.type !== 'transfer_failed')];
};

// Transfer Call Node Component
const TransferCallNode = ({ data, selected, id }) => {
    const onDelete = data.onDelete;
    const connectionHighlight = data.connectionHighlight || false;
    const isGlobalNode = data.isGlobalNode || false;
    const transfer = data.transfer || {};
    const transferType = transfer.transfer_type === 'warm' ? 'warm' : 'cold';
    const transitions = data.transitions || [];
    const failedIndex = transitions.findIndex(t => t.type === 'transfer_failed');
    const triggers = transitions.filter(t => t.type !== 'transfer_failed' && (t.condition || '').trim());

    return (
        <div className={`bg-card rounded-xl shadow-xl shadow-primary/10 border-[3px] transition-all duration-200 ${selected ? 'border-primary' : connectionHighlight ? 'border-primary ring-4 ring-primary/30 ring-offset-4' : 'border-border'}`} style={{ minWidth: '700px', maxWidth: '950px' }}>
            <div className="p-8">
                <div className="mb-6 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <PhoneForwarded size={28} className="text-primary" />
                        <h3 style={{ fontSize: '28px', lineHeight: '34px' }} className="font-bold text-foreground">{data.name || 'Transfer Call'}</h3>
                        {isGlobalNode && (
                            <span className="bg-primary text-primary-foreground font-bold px-3 py-2 rounded" style={{ fontSize: '16px' }}>GLOBAL</span>
                        )}
                        <span className="bg-muted text-foreground font-bold px-3 py-2 rounded border border-border" style={{ fontSize: '16px' }}>{transferType.toUpperCase()}</span>
                    </div>
                    {onDelete && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm(`Are you sure you want to delete "${data.name || 'Transfer Call'}"?`)) {
                                    onDelete(id);
                                }
                            }}
                            className="text-destructive hover:text-destructive/80 hover:bg-destructive/10 p-2 rounded transition-colors"
                            title="Delete node"
                        >
                            <Trash2 size={24} />
                        </button>
                    )}
                </div>

                <div className="mb-5 text-foreground bg-muted/50 rounded-lg p-6 border border-border space-y-2" style={{ fontSize: '20px', lineHeight: '30px' }}>
                    <div><span className="font-semibold">Transfer to:</span> <span className="font-mono">{transfer.target_number || 'No number set'}</span></div>
                    <div><span className="font-semibold">Ring duration:</span> {transfer.ring_duration_seconds || TRANSFER_RING_DEFAULT_S} seconds</div>
                </div>

                <div className="mb-5 text-foreground bg-muted/50 rounded-lg p-6 border border-border" style={{ fontSize: '20px', lineHeight: '30px' }}>
                    <div className="font-semibold mb-3" style={{ fontSize: '20px' }}>Message:</div>
                    <div className="whitespace-pre-wrap">{data.prompt || DEFAULT_TRANSFER_MESSAGES[transferType]}</div>
                </div>

                {isGlobalNode && triggers.length > 0 && (
                    <div className="mb-5 space-y-2">
                        <div className="font-semibold text-foreground" style={{ fontSize: '20px' }}>Triggers when:</div>
                        {triggers.map((t, index) => (
                            <div key={t.id || index} className="text-foreground bg-muted/50 rounded-lg px-5 py-3 border border-border" style={{ fontSize: '18px' }}>
                                • {t.condition}
                            </div>
                        ))}
                    </div>
                )}

                <div className="flex items-center gap-3 group relative">
                    <div className="flex-1 text-foreground bg-muted/50 rounded-lg px-5 py-4 border border-border" style={{ fontSize: '20px', lineHeight: '28px' }}>
                        • Transfer failed (no answer within {transfer.ring_duration_seconds || TRANSFER_RING_DEFAULT_S}s)
                    </div>
                    <Handle
                        type="source"
                        position={Position.Right}
                        id={`transition-${failedIndex >= 0 ? failedIndex : 0}`}
                        className="!bg-primary !border-2 !border-card !w-4 !h-4 !right-[-8px]"
                    />
                </div>
            </div>

            <Handle
                type="target"
                position={Position.Left}
                id="target"
                className="!bg-primary !border-2 !border-card !w-4 !h-4 !left-[-8px]"
            />
        </div>
    );
};

// Custom Edge Component with Delete Button - enhanced for visibility and selection
const CustomEdge = ({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, style = {}, markerEnd, selected, data, source, target, handleEdgeDelete: propHandleEdgeDelete, interactionWidth = 24 }) => {
    const [edgePath, labelX, labelY] = getSmoothStepPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
        borderRadius: 12,
    });

    const strokeWidth = selected ? 5 : 3;
    const strokeColor = selected ? 'hsl(var(--primary))' : (style.stroke || 'hsl(var(--primary))');
    const edgeStyle = { ...style, stroke: strokeColor, strokeWidth };

    return (
        <>
            <BaseEdge path={edgePath} markerEnd={markerEnd} style={edgeStyle} interactionWidth={interactionWidth} />
            <EdgeLabelRenderer>
                <div
                    style={{
                        position: 'absolute',
                        transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
                        pointerEvents: 'all',
                        zIndex: 1000,
                    }}
                    className="nodrag nopan"
                >
                    <button
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('Delete button clicked, edgeId:', id, 'data:', data, 'edge:', { source, target });
                            if (data?.onDelete) {
                                console.log('Calling onDelete for edge:', id);
                                data.onDelete(id, { id, source, target });
                            } else {
                                console.warn('onDelete function not found in edge data, using prop handleEdgeDelete');
                                // For old edges without data.onDelete, use the prop function
                                if (propHandleEdgeDelete) {
                                    propHandleEdgeDelete(id, { id, source, target });
                                }
                            }
                        }}
                        className="edge-delete-button"
                        style={{
                            width: '24px',
                            height: '24px',
                            background: '#ef4444',
                            border: '2px solid white',
                            borderRadius: '50%',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0,
                            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                            opacity: 1,
                            transition: 'all 0.2s',
                            zIndex: 1000,
                        }}
                        title="Delete connection"
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#dc2626';
                            e.currentTarget.style.transform = 'scale(1.15)';
                            e.currentTarget.style.boxShadow = '0 3px 8px rgba(0,0,0,0.4)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#ef4444';
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';
                        }}
                    >
                        <X size={14} color="white" />
                    </button>
                </div>
            </EdgeLabelRenderer>
        </>
    );
};

const nodeTypes = {
    custom: CustomNode,
    customFunctionNode: CustomFunctionNode,
    logicSplitNode: LogicSplitNode,
    transferCallNode: TransferCallNode,
};

// Create a stable edge type component factory
// We'll use a ref to access handleEdgeDelete to avoid recreating the component
let edgeDeleteHandlerRef = null;

const EdgeWithDelete = React.memo((props) => {
    // Use the ref if available, otherwise fall back to props
    const deleteHandler = edgeDeleteHandlerRef || props.data?.onDelete;
    return <CustomEdge {...props} handleEdgeDelete={deleteHandler} />;
});

EdgeWithDelete.displayName = 'EdgeWithDelete';

// Define edgeTypes outside component - it will use the ref
const edgeTypes = {
    default: EdgeWithDelete,
};

const FlowBuilder = forwardRef(({ flowData, onFlowChange, requiredVariables = [] }, ref) => {
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [selectedNode, setSelectedNode] = useState(null);
    const [highlightedNodeIds, setHighlightedNodeIds] = useState(new Set());
    const [showNodeModal, setShowNodeModal] = useState(false);
    const [showCustomFunctionModal, setShowCustomFunctionModal] = useState(false);
    const [showLogicSplitModal, setShowLogicSplitModal] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [transferFormData, setTransferFormData] = useState(null);
    const [editingTransition, setEditingTransition] = useState(null);
    const [configuringConditions, setConfiguringConditions] = useState(null); // transition.id for which we're configuring conditions
    const [logicSplitFormData, setLogicSplitFormData] = useState({
        name: 'Logic Split Node',
        transitions: []
    });
    const [nodeFormData, setNodeFormData] = useState({
        name: '',
        prompt: '',
        prompt_type: 'static_sentence', // 'static_sentence' or 'prompt'
        transitions: [],
        variables_to_extract: [],
        speakDuringExecution: false,
        isEndNode: false,
        isGlobalNode: false,
        skipResponse: false
    });
    const [customFunctionData, setCustomFunctionData] = useState(null);
    const [showImportModal, setShowImportModal] = useState(false);
    const [importJsonText, setImportJsonText] = useState('');
    const [importError, setImportError] = useState('');
    const [exportCopied, setExportCopied] = useState(false);
    const [showClearConfirm, setShowClearConfirm] = useState(false);
    const nodeIdRef = useRef(1);
    const initializedRef = useRef(false);
    const deleteNodeRef = useRef(null);
    const reactFlowRef = useRef(null);
    const flowWrapperRef = useRef(null);
    // Set true by "Clear Flow" so (a) the debounced sync effect's cleanup does NOT
    // re-flush the just-deleted nodes back to the parent, and (b) buildFlowPayload
    // returns an explicit empty flow (instead of null) so Save persists the clear.
    // Reset to false as soon as any node exists again.
    const flowClearedRef = useRef(false);

    React.useEffect(() => {
        const wrapper = flowWrapperRef.current;
        if (!wrapper) return;
        const SENSITIVITY = 0.025;
        let rafId = null;
        let pendingDelta = 0;
        let pendingClientX = 0;
        let pendingClientY = 0;
        const flush = () => {
            rafId = null;
            const rf = reactFlowRef.current;
            if (!rf || pendingDelta === 0) return;
            const rect = wrapper.getBoundingClientRect();
            const cursorX = pendingClientX - rect.left;
            const cursorY = pendingClientY - rect.top;
            const vp = rf.getViewport();
            const delta = -pendingDelta * SENSITIVITY;
            const newZoom = Math.min(Math.max(vp.zoom * Math.pow(2, delta), 0.1), 4);
            const scale = newZoom / vp.zoom;
            rf.setViewport({
                x: cursorX - (cursorX - vp.x) * scale,
                y: cursorY - (cursorY - vp.y) * scale,
                zoom: newZoom,
            });
            pendingDelta = 0;
        };
        const handler = (e) => {
            if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                e.stopPropagation();
                pendingDelta += e.deltaY;
                pendingClientX = e.clientX;
                pendingClientY = e.clientY;
                if (rafId === null) {
                    rafId = requestAnimationFrame(flush);
                }
            }
        };
        wrapper.addEventListener('wheel', handler, { passive: false, capture: true });
        return () => {
            if (rafId !== null) cancelAnimationFrame(rafId);
            wrapper.removeEventListener('wheel', handler, { capture: true });
        };
    }, []);

    // Handle edge deletion - remove the connection from the corresponding transition
    // Defined early so it can be used in initialization
    const handleEdgeDelete = useCallback((edgeId, edgeInfo = null) => {
        console.log('handleEdgeDelete called with edgeId:', edgeId, 'edgeInfo:', edgeInfo);
        
        // Get source and target from edgeInfo if provided, otherwise try to find the edge
        let sourceNodeId, targetNodeId;
        if (edgeInfo && edgeInfo.source && edgeInfo.target) {
            sourceNodeId = edgeInfo.source;
            targetNodeId = edgeInfo.target;
        } else {
            // Try to find the edge in the edges array
            const edge = edges.find(e => e.id === edgeId);
            if (edge) {
                sourceNodeId = edge.source;
                targetNodeId = edge.target;
            }
        }
        
        // Parse transition index from edge ID (for transition-based edges)
        // Format: edge-trans-${node.id}-${transition.target_node_id}-${transIndex}
        // Since node IDs can contain dashes, we parse from the end
        let transIdx = null;
        if (edgeId.startsWith('edge-trans-') && sourceNodeId && targetNodeId) {
            // Extract the index from the end (last number after last dash)
            const lastDashIndex = edgeId.lastIndexOf('-');
            if (lastDashIndex > 0) {
                const indexStr = edgeId.substring(lastDashIndex + 1);
                transIdx = parseInt(indexStr);
                console.log('Parsed transition index from edge ID:', transIdx, 'source:', sourceNodeId, 'target:', targetNodeId);
            }
        }
        
        // Handle transition-based edges (have a transition index) or old ReactFlow edges
        if (sourceNodeId && targetNodeId) {
            
            // Find the source node and update it, then remove the edge
            setNodes((nds) => {
                const updatedNodes = nds.map((node) => {
                    if (node.id === sourceNodeId) {
                        const transitions = node.data.transitions || [];
                        const updatedTransitions = transitions.map((transition, idx) => {
                            // For transition-based edges with index, match by both index and target_node_id
                            // For old ReactFlow edges, match by target_node_id only
                            if (transIdx !== null) {
                                // Match by index and target_node_id
                                if (idx === transIdx && transition.target_node_id === targetNodeId) {
                                    console.log('Clearing target_node_id for transition at index:', idx, 'transition:', transition);
                                    // Clear the target_node_id to remove the connection
                                    return { ...transition, target_node_id: '' };
                                }
                            } else {
                                // Old edge format - match by target_node_id only
                                if (transition.target_node_id === targetNodeId) {
                                    console.log('Clearing target_node_id for transition (old edge format) at index:', idx, 'transition:', transition);
                                    // Clear the target_node_id to remove the connection
                                    return { ...transition, target_node_id: '' };
                                }
                            }
                            return transition;
                        });
                        
                        console.log('Updated transitions:', updatedTransitions);
                        
                        const updatedNode = {
                            ...node,
                            data: {
                                ...node.data,
                                transitions: updatedTransitions,
                            },
                        };
                        
                        // If this is the selected node, also update nodeFormData and selectedNode
                        if (selectedNode && selectedNode.id === sourceNodeId) {
                            console.log('Updating nodeFormData for selected node');
                            setNodeFormData((prev) => ({
                                ...prev,
                                transitions: updatedTransitions,
                            }));
                            // Update selectedNode to point to the updated node
                            setSelectedNode(updatedNode);
                        }
                        
                        return updatedNode;
                    }
                    return node;
                });
                
                // Remove the edge after node update (in the same batch if possible)
                setTimeout(() => {
                    setEdges((eds) => {
                        const filtered = eds.filter((edge) => edge.id !== edgeId);
                        console.log('Removed edge, remaining edges:', filtered.length);
                        return filtered;
                    });
                }, 0);
                
                return updatedNodes;
            });
        } else if (sourceNodeId && targetNodeId) {
            // This is a manually created edge (not from transitions)
            // Find the source node and clear any transition that points to the target
            console.log('Manually created edge - source:', sourceNodeId, 'target:', targetNodeId);
            
            setNodes((nds) => {
                const updatedNodes = nds.map((node) => {
                    if (node.id === sourceNodeId) {
                        const transitions = node.data.transitions || [];
                        const updatedTransitions = transitions.map((transition) => {
                            // Clear target_node_id if it matches the edge target
                            if (transition.target_node_id === targetNodeId) {
                                console.log('Clearing target_node_id for transition:', transition);
                                return { ...transition, target_node_id: '' };
                            }
                            return transition;
                        });
                        
                        const updatedNode = {
                            ...node,
                            data: {
                                ...node.data,
                                transitions: updatedTransitions,
                            },
                        };
                        
                        // If this is the selected node, also update nodeFormData
                        if (selectedNode && selectedNode.id === sourceNodeId) {
                            console.log('Updating nodeFormData for selected node (manual edge)');
                            setNodeFormData((prev) => ({
                                ...prev,
                                transitions: updatedTransitions,
                            }));
                            setSelectedNode(updatedNode);
                        }
                        
                        return updatedNode;
                    }
                    return node;
                });
                
                // Remove the edge after node update
                setTimeout(() => {
                    setEdges((eds) => {
                        const filtered = eds.filter((edge) => edge.id !== edgeId);
                        console.log('Removed edge (manual), remaining edges:', filtered.length);
                        return filtered;
                    });
                }, 0);
                
                return updatedNodes;
            });
        } else {
            console.warn('Edge ID format does not match expected pattern and no edge info provided:', edgeId);
            // Still try to remove the edge even if we can't update the node
            setEdges((eds) => {
                const filtered = eds.filter((edge) => edge.id !== edgeId);
                console.log('Removed edge (fallback), remaining edges:', filtered.length);
                return filtered;
            });
        }
    }, [setNodes, setEdges, selectedNode, edges]);

    // Stable wrapper around handleEdgeDelete for edge data and effect deps.
    // handleEdgeDelete changes with every `edges` change, and the edge rebuild
    // effect calls setEdges, so depending on it directly re-ran that effect
    // forever: about 120 onFlowChange calls a second while idle, each one
    // writing a stale copy of the form back (the Global Prompt cursor jump).
    const handleEdgeDeleteRef = useRef(handleEdgeDelete);
    handleEdgeDeleteRef.current = handleEdgeDelete;
    const stableEdgeDelete = useCallback((...args) => handleEdgeDeleteRef.current(...args), []);

    // Update the ref so EdgeWithDelete can access the latest handleEdgeDelete
    React.useEffect(() => {
        edgeDeleteHandlerRef = handleEdgeDelete;
    }, [handleEdgeDelete]);

    // Highlight connected nodes when an edge is selected - uses onSelectionChange for reliability
    const handleSelectionChange = useCallback(({ nodes: _selectedNodes, edges: selectedEdges }) => {
        const ids = new Set();
        if (selectedEdges && selectedEdges.length > 0) {
            selectedEdges.forEach(e => {
                if (e.source) ids.add(e.source);
                if (e.target) ids.add(e.target);
            });
        }
        setHighlightedNodeIds(ids);
    }, []);

    React.useEffect(() => {
        setNodes(nds => {
            let changed = false;
            const next = nds.map(n => {
                const shouldHighlight = highlightedNodeIds.has(n.id);
                if (n.data?.connectionHighlight === shouldHighlight) return n;
                changed = true;
                return { ...n, data: { ...n.data, connectionHighlight: shouldHighlight } };
            });
            return changed ? next : nds;
        });
    }, [highlightedNodeIds, setNodes]);

    // Collect all available variables (required + extracted from nodes)
    const availableVariables = React.useMemo(() => {
        const vars = new Set();
        
        // Add required variables from agent
        if (requiredVariables && Array.isArray(requiredVariables)) {
            requiredVariables.forEach(v => {
                if (v && v.name) {
                    vars.add(v.name);
                }
            });
        }
        
        // Add variables extracted from all nodes
        nodes.forEach(node => {
            if (node.data && node.data.variables_to_extract && Array.isArray(node.data.variables_to_extract)) {
                node.data.variables_to_extract.forEach(varName => {
                    if (varName) {
                        vars.add(varName);
                    }
                });
            }
        });
        
        return Array.from(vars).sort();
    }, [requiredVariables, nodes]);

    // Helper function to generate condition string from equation object or array
    const generateConditionFromEquation = (equation) => {
        // Handle array of equations
        if (Array.isArray(equation)) {
            if (equation.length === 0) return '';
            if (equation.length === 1) {
                return generateConditionFromEquation(equation[0]);
            }
            // Multiple equations - show first few with ellipsis
            const firstTwo = equation.slice(0, 2).map(eq => {
                if (!eq || !eq.variable || !eq.operator) return '';
                const varName = eq.variable;
                const operator = eq.operator;
                const value = eq.value || '';
                let formattedValue = value;
                if (operator !== 'exists' && operator !== 'not exists') {
                    if (isNaN(value) || value === '') {
                        formattedValue = `"${value}"`;
                    }
                }
                return `{{${varName}}} ${operator} ${formattedValue}`;
            }).filter(s => s).join(', ');
            return equation.length > 2 ? `${firstTwo}, ...` : firstTwo;
        }
        
        // Handle single equation object
        if (!equation || !equation.variable || !equation.operator) {
            return '';
        }
        const varName = equation.variable;
        const operator = equation.operator;
        const value = equation.value || '';
        
        // Format value with quotes if it's a string (not a number)
        let formattedValue = value;
        if (operator !== 'exists' && operator !== 'not exists') {
            if (isNaN(value) || value === '') {
                formattedValue = `"${value}"`;
            }
        }
        
        return `{{${varName}}} ${operator} ${formattedValue}`;
    };

    // Helper to parse variable name from user input (supports {{variable}} or plain variable name)
    const parseVariableFromInput = (input) => {
        if (!input || typeof input !== 'string') return '';
        const trimmed = input.trim();
        const match = trimmed.match(/\{\{([^}]+)\}\}/);
        if (match) return match[1].trim();
        return trimmed.replace(/^\{\{/, '').replace(/\}\}$/, '').trim();
    };

    // Helper function to parse condition string to equation object (for backward compatibility)
    const parseConditionToEquation = (condition) => {
        if (!condition) {
            return { variable: '', operator: '=', value: '' };
        }
        
        // Try to parse format: {{variable}} operator value
        const match = condition.match(/\{\{(\w+)\}\}\s*([!=<>]+|contains|not contains|exists|not exists)\s*(.*)/);
        if (match) {
            const variable = match[1];
            const operator = match[2];
            let value = match[3].trim();
            
            // Remove quotes if present
            if ((value.startsWith('"') && value.endsWith('"')) || 
                (value.startsWith("'") && value.endsWith("'"))) {
                value = value.slice(1, -1);
            }
            
            return { variable, operator, value };
        }
        
        return { variable: '', operator: '=', value: '' };
    };

    // Helper function to migrate old single equation to new array format
    const migrateTransitionEquation = (transition) => {
        // If already migrated, return as is
        if (transition.equations && Array.isArray(transition.equations)) {
            return transition;
        }
        
        // If has old single equation format, migrate it
        if (transition.equation && !transition.equations) {
            transition.equations = [transition.equation];
            transition.equationMatchType = transition.equationMatchType || 'any';
            // Keep equation for backward compatibility during transition period
        } else if (!transition.equations && transition.type === 'equation') {
            // Initialize empty equations array if switching to equation type
            transition.equations = [];
            transition.equationMatchType = transition.equationMatchType || 'any';
        }
        
        return transition;
    };

    // Helper function to add onDelete to node data
    const addDeleteFunctionToNodes = useCallback((nodesArray) => {
        const deleteFn = deleteNodeRef.current;
        if (!deleteFn) return nodesArray; // Return as-is if deleteNode not yet set
        return nodesArray.map((node) => ({
            ...node,
            data: {
                ...node.data,
                onDelete: deleteFn
            }
        }));
    }, []);

    // Delete node function
    const deleteNode = useCallback((nodeId) => {
        // Remove the node and clean up edges
        setNodes((nds) => {
            // Also remove transitions in other nodes that reference this node
            const updatedNodes = nds
                .filter((node) => node.id !== nodeId)
                .map((node) => {
                    if (node.data.transitions && Array.isArray(node.data.transitions)) {
                        return {
                            ...node,
                            data: {
                                ...node.data,
                                transitions: node.data.transitions.filter(
                                    (trans) => trans.target_node_id !== nodeId
                                )
                            }
                        };
                    }
                    return node;
                });
            // Ensure all nodes still have the delete function
            return addDeleteFunctionToNodes(updatedNodes);
        });
        setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
    }, [addDeleteFunctionToNodes]);

    // Update the ref whenever deleteNode changes
    React.useEffect(() => {
        deleteNodeRef.current = deleteNode;
        // Re-apply delete function to all existing nodes
        setNodes((nds) => addDeleteFunctionToNodes(nds));
    }, [deleteNode, addDeleteFunctionToNodes]);

    // Build flow payload for export (same shape as onFlowChange)
    const buildFlowPayload = useCallback(() => {
        // After an explicit "Clear Flow", return an empty flow so Save persists the
        // deletion. Without a clear, an empty canvas returns null (unchanged behavior:
        // an uninitialized/never-built flow should not overwrite anything on save).
        if (nodes.length === 0) {
            return flowClearedRef.current
                ? { nodes: [], edges: [], start_node_id: null }
                : null;
        }
        const startNodeId = nodes[0].id;
        return {
            nodes: nodes.map(node => {
                const hasCustomFunction = node.data.customFunction != null;
                const isCustomFunctionNode = node.type === 'customFunctionNode' && hasCustomFunction;
                const isLogicSplitNode = node.type === 'logicSplitNode';
                const cleanedTransitions = (node.data.transitions || []).map(t => {
                    const migrated = migrateTransitionEquation({ ...t });
                    if (migrated.equations && Array.isArray(migrated.equations) && migrated.equations.length > 0) {
                        const { equation, ...rest } = migrated;
                        return rest;
                    }
                    return migrated;
                });
                const baseNode = {
                    id: node.id,
                    type: isCustomFunctionNode ? 'customFunctionNode' : (isLogicSplitNode ? 'logicSplitNode' : (node.type === 'transferCallNode' ? 'transferCallNode' : undefined)),
                    name: node.data.name || node.data.label,
                    prompt: node.data.prompt || '',
                    prompt_type: node.data.prompt_type || 'static_sentence',
                    transitions: cleanedTransitions,
                    variables_to_extract: node.data.variables_to_extract || [],
                    isEndNode: node.data.isEndNode || false,
                    isGlobalNode: node.data.isGlobalNode || false,
                    skipResponse: node.data.skipResponse || false,
                    position: node.position
                };
                if (node.type === 'transferCallNode') {
                    baseNode.transfer = node.data.transfer || {};
                }
                if (isLogicSplitNode && node.data.isGlobalNode) {
                    baseNode.globalTriggers = node.data.globalTriggers || [];
                }
                if (isCustomFunctionNode) {
                    baseNode.data = {
                        nodeName: node.data.name || node.data.label,
                        prompt: node.data.prompt || '',
                        speakDuringExecution: node.data.speakDuringExecution || false,
                        transitions: node.data.transitions || baseNode.transitions || [],
                        customFunction: node.data.customFunction
                    };
                    baseNode.transitions = node.data.transitions || baseNode.transitions || [];
                }
                return baseNode;
            }),
            edges: edges.map(edge => ({
                id: edge.id,
                source: edge.source,
                target: edge.target,
                sourceHandle: edge.sourceHandle,
                targetHandle: edge.targetHandle
            })),
            start_node_id: nodes[0].id
        };
    }, [nodes, edges]);

    // Expose getCurrentFlow so parent can flush pending flow data before save (avoids debounce race).
    // reloadFromFlowData: the canvas reads flowData only once, so a parent that
    // replaces the flow from outside (the maximized editor window) calls this
    // first and the next flowData prop rebuilds the canvas, same as Import JSON.
    // Without it the canvas kept the old nodes and getCurrentFlow put them back on Save.
    useImperativeHandle(ref, () => ({
        getCurrentFlow: () => buildFlowPayload(),
        reloadFromFlowData: () => {
            initializedRef.current = false;
            flowClearedRef.current = false;
        }
    }), [buildFlowPayload]);

    const handleExportJson = useCallback(() => {
        const payload = buildFlowPayload();
        if (!payload) return;
        const json = JSON.stringify(payload, null, 2);
        navigator.clipboard.writeText(json).then(() => {
            setExportCopied(true);
            setTimeout(() => setExportCopied(false), 2000);
        }).catch(() => {});
    }, [buildFlowPayload]);

    const handleImportJson = useCallback(() => {
        setImportError('');
        try {
            const parsed = JSON.parse(importJsonText.trim());
            if (!parsed || !Array.isArray(parsed.nodes) || parsed.nodes.length === 0) {
                setImportError('Invalid flow: must have a "nodes" array with at least one node.');
                return;
            }
            const normalized = {
                nodes: parsed.nodes,
                edges: Array.isArray(parsed.edges) ? parsed.edges : [],
                start_node_id: parsed.start_node_id || (parsed.nodes[0] && parsed.nodes[0].id) || 'node-1'
            };
            initializedRef.current = false;
            onFlowChange(normalized);
            setShowImportModal(false);
            setImportJsonText('');
        } catch (e) {
            setImportError(e.message || 'Invalid JSON');
        }
    }, [importJsonText, onFlowChange]);

    // Clear the entire flow — every node and edge. Called after the user confirms.
    const handleClearFlow = useCallback(() => {
        // Mark as cleared so the sync-effect cleanup won't re-flush the old nodes
        // and so buildFlowPayload/getCurrentFlow report an empty flow on Save.
        flowClearedRef.current = true;
        setSelectedNode(null);
        setHighlightedNodeIds(new Set());
        setShowNodeModal(false);
        setShowCustomFunctionModal(false);
        setShowLogicSplitModal(false);
        setShowTransferModal(false);
        setEdges([]);
        setNodes([]);
        // Notify the parent immediately so formData.conversation_flow reflects the
        // empty flow (getCurrentFlow returns null-shaped empty; this keeps them in sync).
        if (onFlowChange) {
            onFlowChange({ nodes: [], edges: [], start_node_id: null });
        }
        setShowClearConfirm(false);
    }, [onFlowChange, setNodes, setEdges]);

    // Initialize nodes and edges from flowData
    React.useEffect(() => {
        if (initializedRef.current) return; // Only initialize once
        
        // Helper function to create edge with delete handler
        const createEdgeWithDelete = (edgeId, source, target, sourceHandle, targetHandle) => ({
            id: edgeId,
            source,
            target,
            sourceHandle,
            targetHandle,
            type: 'default',
            className: 'nodrag nopan',
            style: { stroke: 'hsl(var(--primary))', strokeWidth: 3 },
            data: {
                onDelete: stableEdgeDelete
            }
        });
        
        if (flowData && flowData.nodes && Array.isArray(flowData.nodes) && flowData.nodes.length > 0) {
            // Convert backend format to ReactFlow format
            const reactFlowNodes = flowData.nodes.map((node, index) => {
                // Ensure node has required properties
                const nodeId = node.id || `node-${index + 1}`;
                const nodeName = node.name || 'Unnamed Node';
                const nodeData = node.data || {};
                // Check node type
                const isCustomFunctionNode = node.type === 'customFunctionNode' || (nodeData && nodeData.customFunction);
                const isLogicSplitNode = node.type === 'logicSplitNode';
                const isTransferCallNode = node.type === 'transferCallNode';
                let nodeType = 'custom';
                if (isCustomFunctionNode) nodeType = 'customFunctionNode';
                else if (isLogicSplitNode) nodeType = 'logicSplitNode';
                else if (isTransferCallNode) nodeType = 'transferCallNode';
                const loadedTransitions = (Array.isArray(node.transitions) ? node.transitions : (Array.isArray(nodeData.transitions) ? nodeData.transitions : [])).map(t => migrateTransitionEquation(t));
                
                return {
                    id: nodeId,
                    type: nodeType,
                    position: (node.position && typeof node.position === 'object' && 'x' in node.position && 'y' in node.position) 
                        ? { x: node.position.x, y: node.position.y }
                        : { 
                            x: 250 + (index % 3) * 400, 
                            y: 100 + Math.floor(index / 3) * 250 
                        },
                    data: {
                        label: nodeName,
                        name: nodeName,
                        prompt: node.prompt || nodeData.prompt || '',
                        prompt_type: node.prompt_type || nodeData.prompt_type || 'static_sentence', // Default to static_sentence for backward compatibility
                        transitions: isTransferCallNode ? normalizeTransferTransitions(loadedTransitions) : loadedTransitions,
                        variables_to_extract: Array.isArray(node.variables_to_extract) ? node.variables_to_extract : [],
                        speakDuringExecution: nodeData.speakDuringExecution || false,
                        isEndNode: node.isEndNode || nodeData.isEndNode || false, // Check both top-level and nested
                        isGlobalNode: node.isGlobalNode || nodeData.isGlobalNode || false, // Check both top-level and nested
                        skipResponse: node.skipResponse || nodeData.skipResponse || false, // Check both top-level and nested
                        customFunction: nodeData.customFunction || null,
                        ...(isTransferCallNode ? { transfer: node.transfer || nodeData.transfer || {} } : {}),
                        ...(isLogicSplitNode ? { globalTriggers: node.globalTriggers || nodeData.globalTriggers || [] } : {})
                    }
                };
            });
            setNodes(reactFlowNodes);
            
            // Convert edges - also create edges from transitions
            const allEdges = [];
            
            // Add explicit edges from flowData
            if (flowData.edges && Array.isArray(flowData.edges) && flowData.edges.length > 0) {
                flowData.edges.forEach((edge, index) => {
                    if (edge.source && edge.target) {
                        allEdges.push({
                            id: edge.id || `edge-${edge.source}-${edge.target}-${index}`,
                            source: edge.source,
                            target: edge.target,
                            sourceHandle: edge.sourceHandle,
                            targetHandle: edge.targetHandle,
                            type: 'default',
                            className: 'nodrag nopan',
                            style: { stroke: 'hsl(var(--primary))', strokeWidth: 3 },
                            data: {
                                onDelete: stableEdgeDelete
                            }
                        });
                    }
                });
            }
            
            // Create edges from transitions
            reactFlowNodes.forEach((node) => {
                if (node.data.transitions && Array.isArray(node.data.transitions)) {
                    const isLogicSplitNode = node.type === 'logicSplitNode';
                    node.data.transitions.forEach((transition, transIndex) => {
                        if (transition.target_node_id) {
                            const edgeId = `edge-trans-${node.id}-${transition.target_node_id}-${transIndex}`;
                            // Check if edge already exists
                            if (!allEdges.find(e => e.id === edgeId)) {
                                // For logic split nodes, use 'transition-else' for else transitions, otherwise use index
                                let sourceHandle;
                                if (isLogicSplitNode && transition.isElse) {
                                    sourceHandle = 'transition-else';
                                } else if (isLogicSplitNode) {
                                    // For regular transitions in logic split nodes, use the index in the filtered array
                                    const regularTransitions = node.data.transitions.filter(t => !t.isElse);
                                    const regularIndex = regularTransitions.findIndex(t => t.id === transition.id);
                                    sourceHandle = `transition-${regularIndex}`;
                                } else {
                                    sourceHandle = `transition-${transIndex}`;
                                }
                                allEdges.push(createEdgeWithDelete(
                                    edgeId,
                                    node.id,
                                    transition.target_node_id,
                                    sourceHandle,
                                    'target'
                                ));
                            }
                        }
                    });
                }
            });
            
            setEdges(allEdges);
            
            // Update nodeIdRef
            const maxId = Math.max(0, ...flowData.nodes.map(n => {
                if (!n.id) return 0;
                const match = n.id.match(/node-(\d+)/);
                return match ? parseInt(match[1]) : 0;
            }));
            nodeIdRef.current = maxId + 1;
            initializedRef.current = true;
        } else {
            // Initialize with start node if no flow data
            const startNode = {
                id: 'node-1',
                type: 'custom',
                position: { x: 250, y: 100 },
                data: {
                    label: 'Start',
                    name: 'Start',
                    prompt: 'Welcome! How can I help you?',
                    transitions: [],
                    variables_to_extract: []
                }
            };
            setNodes([startNode]);
            nodeIdRef.current = 2;
            initializedRef.current = true;
        }
    }, [flowData, stableEdgeDelete]);

    // Sync nodeFormData with selectedNode when nodes change (for edge deletion updates)
    React.useEffect(() => {
        if (selectedNode && showNodeModal) {
            const currentNode = nodes.find(n => n.id === selectedNode.id);
            if (currentNode && currentNode.data.transitions) {
                // Check if transitions have changed
                const currentTransitions = JSON.stringify(currentNode.data.transitions);
                const formTransitions = JSON.stringify(nodeFormData.transitions);
                if (currentTransitions !== formTransitions) {
                    setNodeFormData((prev) => ({
                        ...prev,
                        transitions: currentNode.data.transitions || [],
                    }));
                    setSelectedNode(currentNode);
                }
            }
        }
    }, [nodes, selectedNode?.id, showNodeModal]);

    // Ensure all nodes have the delete function (handles ReactFlow internal updates)
    React.useEffect(() => {
        if (!deleteNodeRef.current) return; // Wait for deleteNode to be set
        const nodesWithoutDelete = nodes.filter(node => !node.data.onDelete);
        if (nodesWithoutDelete.length > 0) {
            setNodes((nds) => addDeleteFunctionToNodes(nds));
        }
    }, [nodes.length, addDeleteFunctionToNodes, deleteNode]); // Only check when node count changes or deleteNode is set

    const nodesTransitionKey = React.useMemo(
        () => nodes.map(n => `${n.id}-${JSON.stringify(n.data?.transitions || [])}`).join('|'),
        [nodes]
    );

    // Rebuild edges from transitions whenever nodes change
    React.useEffect(() => {
        if (initializedRef.current && nodes.length > 0) {
            const transitionEdges = [];
            nodes.forEach((node) => {
                if (node.data.transitions && Array.isArray(node.data.transitions)) {
                    const isLogicSplitNode = node.type === 'logicSplitNode';
                    node.data.transitions.forEach((transition, transIndex) => {
                        // Only create edge if target_node_id exists and is not empty
                        if (transition.target_node_id && transition.target_node_id.trim() !== '') {
                            const edgeId = `edge-trans-${node.id}-${transition.target_node_id}-${transIndex}`;
                            // For logic split nodes, use 'transition-else' for else transitions, otherwise use index
                            let sourceHandle;
                            if (isLogicSplitNode && transition.isElse) {
                                sourceHandle = 'transition-else';
                            } else if (isLogicSplitNode) {
                                // For regular transitions in logic split nodes, use the index in the filtered array
                                const regularTransitions = node.data.transitions.filter(t => !t.isElse);
                                const regularIndex = regularTransitions.findIndex(t => t.id === transition.id);
                                sourceHandle = `transition-${regularIndex}`;
                            } else {
                                sourceHandle = `transition-${transIndex}`;
                            }
                            transitionEdges.push({
                                id: edgeId,
                                source: node.id,
                                target: transition.target_node_id,
                                sourceHandle: sourceHandle,
                                targetHandle: 'target',
                                type: 'default',
                                className: 'nodrag nopan',
                                style: { stroke: 'hsl(var(--primary))', strokeWidth: 3 },
                                data: {
                                    onDelete: stableEdgeDelete
                                }
                            });
                        }
                    });
                }
            });
            
            // Keep manually created edges (those not from transitions)
            // Also add delete handler to old edges that don't have it
            const manualEdges = edges
                .filter(e => !e.id || !e.id.startsWith('edge-trans-'))
                .map(edge => ({
                    ...edge,
                    className: edge.className || 'nodrag nopan',
                    style: { stroke: 'hsl(var(--primary))', strokeWidth: 3, ...(edge.style || {}) },
                    data: {
                        ...edge.data,
                        onDelete: edge.data?.onDelete || stableEdgeDelete
                    }
                }));
            const allEdges = [...manualEdges, ...transitionEdges];
            setEdges(allEdges);
        }
    }, [nodesTransitionKey, stableEdgeDelete]);
    
    // Notify parent of flow changes (debounced to avoid lag during drag/pan)
    const onFlowChangeRef = React.useRef(onFlowChange);
    onFlowChangeRef.current = onFlowChange;
    React.useEffect(() => {
        if (!onFlowChangeRef.current || nodes.length === 0 || !initializedRef.current) return;
        // Nodes exist again → a prior "Clear Flow" is over; resume normal flushing.
        flowClearedRef.current = false;
        const id = setTimeout(() => {
            const startNodeId = nodes[0].id;
            onFlowChangeRef.current?.({
                nodes: nodes.map(node => {
                    // Only mark as customFunctionNode if it has customFunction data (not null/undefined)
                    const hasCustomFunction = node.data.customFunction != null;
                    const isCustomFunctionNode = node.type === 'customFunctionNode' && hasCustomFunction;
                    const isLogicSplitNode = node.type === 'logicSplitNode';
                    
                    // Clean up transitions: migrate old format and remove old equation field
                    const cleanedTransitions = (node.data.transitions || []).map(transition => {
                        const migrated = migrateTransitionEquation({ ...transition });
                        // Remove old equation field if equations array exists
                        if (migrated.equations && Array.isArray(migrated.equations) && migrated.equations.length > 0) {
                            const { equation, ...rest } = migrated;
                            return rest;
                        }
                        return migrated;
                    });
                    
                    const baseNode = {
                        id: node.id,
                        type: isCustomFunctionNode ? 'customFunctionNode' : (isLogicSplitNode ? 'logicSplitNode' : (node.type === 'transferCallNode' ? 'transferCallNode' : undefined)),
                        name: node.data.name || node.data.label,
                        prompt: node.data.prompt || '',
                        prompt_type: node.data.prompt_type || 'static_sentence', // Include prompt_type
                        transitions: cleanedTransitions,
                        variables_to_extract: node.data.variables_to_extract || [],
                        isEndNode: node.data.isEndNode || false,
                        isGlobalNode: node.data.isGlobalNode || false,
                        skipResponse: node.data.skipResponse || false,
                        position: node.position // Preserve position for ReactFlow
                    };
                    if (node.type === 'transferCallNode') {
                        baseNode.transfer = node.data.transfer || {};
                    }
                    if (isLogicSplitNode && node.data.isGlobalNode) {
                        baseNode.globalTriggers = node.data.globalTriggers || [];
                    }
                    
                    // Include custom function data if it's a custom function node with data
                    if (isCustomFunctionNode) {
                        baseNode.data = {
                            nodeName: node.data.name || node.data.label,
                            prompt: node.data.prompt || '',
                            speakDuringExecution: node.data.speakDuringExecution || false,
                            transitions: node.data.transitions || baseNode.transitions || [],
                            customFunction: node.data.customFunction
                        };
                        // Also keep transitions at the top level for consistency
                        baseNode.transitions = node.data.transitions || baseNode.transitions || [];
                    }
                    
                    return baseNode;
                }),
                edges: edges.map(edge => ({
                    id: edge.id,
                    source: edge.source,
                    target: edge.target,
                    sourceHandle: edge.sourceHandle,
                    targetHandle: edge.targetHandle
                })),
                start_node_id: startNodeId
            });
        }, 150);
        return () => clearTimeout(id);
    }, [nodes, edges, buildFlowPayload]);

    // Flush on unmount (e.g. switching tabs) so we don't lose unsaved flow data.
    // Only on unmount: flushing on every re-run of the effect above sent the flow
    // to the parent on every node/edge change, bypassing the debounce.
    // Skip when we just cleared the flow — otherwise this would re-flush the
    // deleted nodes and undo the clear.
    const buildFlowPayloadRef = useRef(buildFlowPayload);
    buildFlowPayloadRef.current = buildFlowPayload;
    React.useEffect(() => () => {
        if (onFlowChangeRef.current && initializedRef.current && !flowClearedRef.current) {
            const payload = buildFlowPayloadRef.current();
            if (payload) onFlowChangeRef.current(payload);
        }
    }, []);
    
    const onConnect = useCallback(
        (params) => {
            const newEdge = {
                ...params,
                type: 'default',
                className: 'nodrag nopan',
                style: { stroke: 'hsl(var(--primary))', strokeWidth: 3 },
                data: {
                    onDelete: stableEdgeDelete
                }
            };

            setNodes((nds) => {
                return nds.map((node) => {
                    if (node.id !== params.source) return node;
                    const transitions = node.data.transitions || [];
                    const sourceHandle = params.sourceHandle;

                    // Find which transition to update based on the handle we dragged from
                    let transitionToUpdate = null;
                    if (sourceHandle === 'transition-else') {
                        transitionToUpdate = transitions.find(t => t.isElse);
                    } else if (sourceHandle) {
                        const match = sourceHandle.match(/transition-(\d+)/);
                        if (match) {
                            const idx = parseInt(match[1], 10);
                            transitionToUpdate = transitions[idx];
                        }
                    }

                    if (transitionToUpdate) {
                        // Update the transition we dragged from (don't create a duplicate)
                        const updatedTransitions = transitions.map(t =>
                            t.id === transitionToUpdate.id
                                ? { ...t, target_node_id: params.target }
                                : t
                        );
                        return {
                            ...node,
                            data: { ...node.data, transitions: updatedTransitions }
                        };
                    }

                    // No matching handle (e.g. node with no transitions yet) - create one
                    const newTransition = {
                        id: `trans-${Date.now()}-${Math.random()}`,
                        condition: '',
                        type: 'prompt',
                        target_node_id: params.target
                    };
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            transitions: [...transitions, newTransition]
                        }
                    };
                });
            });

            setEdges((eds) => addEdge(newEdge, eds));
        },
        [setEdges, setNodes, stableEdgeDelete]
    );

    const addNode = (nodeType = 'custom') => {
        const newNodeId = `node-${nodeIdRef.current++}`;
        const isEndNode = nodeType === 'endNode';
        const isLogicSplit = nodeType === 'logicSplitNode';
        const isTransferCall = nodeType === 'transferCallNode';

        // Place new node at center of current viewport
        let position = { x: 250, y: 100 };
        const rf = reactFlowRef.current;
        const pane = flowWrapperRef.current;
        if (rf && pane) {
            const { x, y, zoom } = rf.getViewport();
            const { clientWidth, clientHeight } = pane;
            position = {
                x: (clientWidth / 2 - x) / zoom,
                y: (clientHeight / 2 - y) / zoom
            };
        }

        const newNode = {
            id: newNodeId,
            type: isEndNode ? 'custom' : nodeType, // End nodes use 'custom' type but with isEndNode flag
            position,
            data: {
                label: nodeType === 'customFunctionNode' ? 'Custom Function' : (isEndNode ? 'End Node' : (isLogicSplit ? 'Logic Split Node' : (isTransferCall ? 'Transfer Call' : `Node ${nodeIdRef.current - 1}`))),
                name: nodeType === 'customFunctionNode' ? 'Custom Function' : (isEndNode ? 'End Node' : (isLogicSplit ? 'Logic Split Node' : (isTransferCall ? 'Transfer Call' : `Node ${nodeIdRef.current - 1}`))),
                prompt: '',
                prompt_type: 'static_sentence', // Default to static_sentence
                transitions: isLogicSplit ? [{ id: `else-${Date.now()}`, isElse: true, condition: '', type: 'prompt', target_node_id: '' }] : (isTransferCall ? [makeTransferFailedRoute()] : []),
                variables_to_extract: [],
                speakDuringExecution: false,
                isEndNode: isEndNode,
                isGlobalNode: false,
                skipResponse: false,
                customFunction: nodeType === 'customFunctionNode' ? null : undefined,
                ...(isTransferCall ? { transfer: { transfer_type: 'cold', target_number: '', ring_duration_seconds: TRANSFER_RING_DEFAULT_S, failure_message: '' } } : {})
            }
        };
        setNodes((nds) => addDeleteFunctionToNodes([...nds, newNode]));
        // Open appropriate modal after state update
        setTimeout(() => {
            if (nodeType === 'customFunctionNode') {
                openCustomFunctionModal(newNode);
            } else if (nodeType === 'logicSplitNode') {
                openLogicSplitModal(newNode);
            } else if (isTransferCall) {
                openTransferModal(newNode);
            } else {
                openNodeModal(newNode);
            }
        }, 0);
    };


    // Auto-layout: organize nodes in a clean left-to-right DAG layout
    const autoLayoutFlow = useCallback(() => {
        if (nodes.length === 0) return;

        const NODE_WIDTH = 950;
        const NODE_HEIGHT = 700;
        const H_GAP = 500;
        const V_GAP = 300;

        // Build adjacency from transitions
        const childrenOf = {};
        const parentOf = {};
        nodes.forEach(n => { childrenOf[n.id] = []; parentOf[n.id] = []; });
        nodes.forEach(n => {
            (n.data.transitions || []).forEach(t => {
                if (t.target_node_id && childrenOf[t.target_node_id]) {
                    childrenOf[n.id].push(t.target_node_id);
                    parentOf[t.target_node_id].push(n.id);
                }
            });
        });

        // Also use edges for any connections not in transitions
        edges.forEach(e => {
            if (childrenOf[e.source] && childrenOf[e.target]) {
                if (!childrenOf[e.source].includes(e.target)) {
                    childrenOf[e.source].push(e.target);
                }
                if (!parentOf[e.target].includes(e.source)) {
                    parentOf[e.target].push(e.source);
                }
            }
        });

        // Find roots (no parents), fallback to first node
        let roots = nodes.filter(n => parentOf[n.id].length === 0).map(n => n.id);
        if (roots.length === 0) roots = [nodes[0].id];

        // Assign layers via BFS (longest path from any root for each node)
        const layer = {};
        const visited = new Set();
        const queue = [];

        roots.forEach(r => { layer[r] = 0; queue.push(r); visited.add(r); });

        while (queue.length > 0) {
            const current = queue.shift();
            const children = [...new Set(childrenOf[current] || [])];
            children.forEach(child => {
                const newLayer = (layer[current] || 0) + 1;
                if (layer[child] === undefined || newLayer > layer[child]) {
                    layer[child] = newLayer;
                }
                if (!visited.has(child)) {
                    visited.add(child);
                    queue.push(child);
                }
            });
        }

        // Assign unvisited/disconnected nodes to their own layer at the end
        const maxLayer = Math.max(0, ...Object.values(layer).filter(v => v !== undefined));
        let disconnectedLayer = maxLayer + 1;
        nodes.forEach(n => {
            if (layer[n.id] === undefined) {
                layer[n.id] = disconnectedLayer;
            }
        });

        // Group nodes by layer
        const layers = {};
        nodes.forEach(n => {
            const l = layer[n.id];
            if (!layers[l]) layers[l] = [];
            layers[l].push(n.id);
        });

        // Sort within each layer to minimize crossing: order children by parent position
        const layerKeys = Object.keys(layers).map(Number).sort((a, b) => a - b);
        const positionInLayer = {};

        // First layer: keep original order or sort by name
        if (layers[layerKeys[0]]) {
            layers[layerKeys[0]].forEach((nid, idx) => { positionInLayer[nid] = idx; });
        }

        // Subsequent layers: order by average parent position
        for (let i = 1; i < layerKeys.length; i++) {
            const layerNodes = layers[layerKeys[i]];
            layerNodes.sort((a, b) => {
                const avgA = parentOf[a].length > 0
                    ? parentOf[a].reduce((sum, p) => sum + (positionInLayer[p] || 0), 0) / parentOf[a].length
                    : 0;
                const avgB = parentOf[b].length > 0
                    ? parentOf[b].reduce((sum, p) => sum + (positionInLayer[p] || 0), 0) / parentOf[b].length
                    : 0;
                return avgA - avgB;
            });
            layerNodes.forEach((nid, idx) => { positionInLayer[nid] = idx; });
        }

        // Calculate positions
        const newPositions = {};
        layerKeys.forEach(l => {
            const layerNodes = layers[l];
            const totalHeight = layerNodes.length * NODE_HEIGHT + (layerNodes.length - 1) * V_GAP;
            const startY = -totalHeight / 2;
            layerNodes.forEach((nid, idx) => {
                newPositions[nid] = {
                    x: l * (NODE_WIDTH + H_GAP),
                    y: startY + idx * (NODE_HEIGHT + V_GAP)
                };
            });
        });

        // Apply positions
        setNodes(nds => nds.map(n => ({
            ...n,
            position: newPositions[n.id] || n.position
        })));

        // Fit view after layout with a small delay for React to render
        setTimeout(() => {
            if (reactFlowRef.current) {
                reactFlowRef.current.fitView({ padding: 0.15, minZoom: 0.45, duration: 400 });
            }
        }, 50);
    }, [nodes, edges, setNodes]);

    const openNodeModal = (node) => {
        setSelectedNode(node);
        setNodeFormData({
            name: node.data.name || node.data.label || '',
            prompt: node.data.prompt || '',
            prompt_type: node.data.prompt_type || 'static_sentence',
            transitions: node.data.transitions || [],
            variables_to_extract: node.data.variables_to_extract || [],
            speakDuringExecution: node.data.speakDuringExecution || false,
            isEndNode: node.data.isEndNode || false,
            isGlobalNode: node.data.isGlobalNode || false,
            skipResponse: node.data.skipResponse || false
        });
        setShowNodeModal(true);
    };

    const openCustomFunctionModal = (node) => {
        setSelectedNode(node);
        setCustomFunctionData(node.data.customFunction || null);
        setNodeFormData({
            name: node.data.name || node.data.label || '',
            prompt: node.data.prompt || '',
            transitions: node.data.transitions || [],
            speakDuringExecution: node.data.speakDuringExecution || false
        });
        setShowCustomFunctionModal(true);
    };

    const openLogicSplitModal = (node) => {
        setSelectedNode(node);
        const transitions = node.data.transitions || [];
        const hasElse = transitions.some(t => t.isElse);
        const transitionsWithElse = hasElse ? transitions : [...transitions, { id: `else-${Date.now()}`, isElse: true, condition: '', type: 'prompt', target_node_id: '' }];
        setLogicSplitFormData({
            name: node.data.name || node.data.label || 'Logic Split Node',
            transitions: transitionsWithElse,
            isGlobalNode: node.data.isGlobalNode || false,
            globalTriggers: (node.data.globalTriggers || []).map(t => ({ id: t.id || `trig-${Date.now()}-${Math.random()}`, condition: t.condition || '' })),
            error: ''
        });
        setShowLogicSplitModal(true);
    };

    const openTransferModal = (node) => {
        setSelectedNode(node);
        const transfer = node.data.transfer || {};
        setTransferFormData({
            name: node.data.name || node.data.label || 'Transfer Call',
            prompt: node.data.prompt || '',
            isGlobalNode: node.data.isGlobalNode || false,
            triggers: (node.data.transitions || [])
                .filter(t => t.type !== 'transfer_failed')
                .map(t => ({ id: t.id || `trans-${Date.now()}-${Math.random()}`, condition: t.condition || '' })),
            transfer_type: transfer.transfer_type === 'warm' ? 'warm' : 'cold',
            target_number: transfer.target_number || '',
            ring_duration_seconds: transfer.ring_duration_seconds || TRANSFER_RING_DEFAULT_S,
            failure_message: transfer.failure_message || '',
            failed_target_node_id: ((node.data.transitions || []).find(t => t.type === 'transfer_failed') || {}).target_node_id || '',
            error: ''
        });
        setShowTransferModal(true);
    };

    const closeTransferModal = () => {
        setShowTransferModal(false);
        setSelectedNode(null);
        setTransferFormData(null);
    };

    const saveTransferNode = () => {
        if (!selectedNode || !transferFormData) return;
        const target = transferFormData.target_number.trim();
        const usesVariable = /\{\{[^}]+\}\}/.test(target);
        if (!target) {
            setTransferFormData({ ...transferFormData, error: 'Enter the phone number to transfer to.' });
            return;
        }
        if (!usesVariable && !/^\+[1-9]\d{7,14}$/.test(target.replace(/[\s().-]/g, ''))) {
            setTransferFormData({ ...transferFormData, error: 'Use E.164 format, for example +15551234567, or a {{variable}}.' });
            return;
        }
        const ring = Math.round(Number(transferFormData.ring_duration_seconds));
        if (!Number.isFinite(ring) || ring < TRANSFER_RING_MIN_S || ring > TRANSFER_RING_MAX_S) {
            setTransferFormData({ ...transferFormData, error: `Transfer ring duration must be between ${TRANSFER_RING_MIN_S} and ${TRANSFER_RING_MAX_S} seconds.` });
            return;
        }
        const triggers = transferFormData.isGlobalNode
            ? transferFormData.triggers
                .filter(t => t.condition.trim())
                .map(t => ({ id: t.id, type: 'prompt', condition: t.condition.trim(), target_node_id: '' }))
            : [];
        if (transferFormData.isGlobalNode && triggers.length === 0) {
            setTransferFormData({ ...transferFormData, error: 'A global transfer node needs at least one trigger condition.' });
            return;
        }

        setNodes((nds) =>
            addDeleteFunctionToNodes(
                nds.map((node) => {
                    if (node.id !== selectedNode.id) return node;
                    // Keep the failure route (and wherever it is connected) as it is on the canvas now
                    const failedRoute = {
                        ...((node.data.transitions || []).find(t => t.type === 'transfer_failed') || makeTransferFailedRoute()),
                        target_node_id: transferFormData.failed_target_node_id || ''
                    };
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            name: transferFormData.name,
                            label: transferFormData.name,
                            prompt: transferFormData.prompt,
                            prompt_type: 'static_sentence',
                            isGlobalNode: transferFormData.isGlobalNode,
                            transitions: [failedRoute, ...triggers],
                            transfer: {
                                transfer_type: transferFormData.transfer_type,
                                target_number: usesVariable ? target : target.replace(/[\s().-]/g, ''),
                                ring_duration_seconds: ring,
                                failure_message: transferFormData.failure_message.trim()
                            }
                        }
                    };
                })
            )
        );
        closeTransferModal();
    };

    const saveLogicSplit = () => {
        if (!selectedNode) return;
        const globalTriggers = logicSplitFormData.isGlobalNode
            ? (logicSplitFormData.globalTriggers || [])
                .filter(t => t.condition.trim())
                .map(t => ({ id: t.id, type: 'prompt', condition: t.condition.trim() }))
            : [];
        if (logicSplitFormData.isGlobalNode && globalTriggers.length === 0) {
            setLogicSplitFormData({ ...logicSplitFormData, error: 'A global logic split needs at least one trigger condition.' });
            return;
        }

        setNodes((nds) =>
            addDeleteFunctionToNodes(
                nds.map((node) => {
                    if (node.id === selectedNode.id) {
                        return {
                            ...node,
                            data: {
                                ...node.data,
                                name: logicSplitFormData.name,
                                label: logicSplitFormData.name,
                                transitions: logicSplitFormData.transitions,
                                isGlobalNode: logicSplitFormData.isGlobalNode || false,
                                globalTriggers
                            }
                        };
                    }
                    return node;
                })
            )
        );
        setShowLogicSplitModal(false);
        setSelectedNode(null);
    };

    const addLogicSplitTransition = () => {
        const newTransition = {
            id: `trans-${Date.now()}`,
            condition: '',
            type: 'prompt',
            target_node_id: '',
            isElse: false
        };
        setLogicSplitFormData({
            ...logicSplitFormData,
            transitions: [...logicSplitFormData.transitions.filter(t => !t.isElse), newTransition, ...logicSplitFormData.transitions.filter(t => t.isElse)]
        });
        setEditingTransition(newTransition.id);
    };

    const updateLogicSplitTransition = (transitionId, updates) => {
        setLogicSplitFormData({
            ...logicSplitFormData,
            transitions: logicSplitFormData.transitions.map((t) =>
                t.id === transitionId ? { ...t, ...updates } : t
            )
        });
    };

    const deleteLogicSplitTransition = (transitionId) => {
        // Don't allow deleting the Else transition
        const transition = logicSplitFormData.transitions.find(t => t.id === transitionId);
        if (transition && transition.isElse) return;
        
        setLogicSplitFormData({
            ...logicSplitFormData,
            transitions: logicSplitFormData.transitions.filter((t) => t.id !== transitionId)
        });
    };

    const saveNode = () => {
        if (!selectedNode) return;

        setNodes((nds) =>
            addDeleteFunctionToNodes(
                nds.map((node) => {
                    if (node.id === selectedNode.id) {
                        return {
                            ...node,
                            data: {
                                ...node.data,
                                label: nodeFormData.name,
                                name: nodeFormData.name,
                                prompt: nodeFormData.prompt,
                                prompt_type: nodeFormData.prompt_type,
                                transitions: (nodeFormData.isEndNode && !nodeFormData.isGlobalNode) ? [] : nodeFormData.transitions, // End nodes without global flag shouldn't have transitions, but global end nodes need them
                                variables_to_extract: nodeFormData.variables_to_extract,
                                speakDuringExecution: nodeFormData.speakDuringExecution,
                                isEndNode: nodeFormData.isEndNode,
                                isGlobalNode: nodeFormData.isGlobalNode,
                                skipResponse: nodeFormData.skipResponse
                            }
                        };
                    }
                    return node;
                })
            )
        );
        setShowNodeModal(false);
        setSelectedNode(null);
    };

    const saveCustomFunction = (data) => {
        if (!selectedNode) return;

        setNodes((nds) =>
            addDeleteFunctionToNodes(
                nds.map((node) => {
                    if (node.id === selectedNode.id) {
                        return {
                            ...node,
                            data: {
                                ...node.data,
                                label: data.nodeData.name,
                                name: data.nodeData.name,
                                prompt: data.nodeData.prompt,
                                transitions: data.transitions || [],
                                speakDuringExecution: data.nodeData.speakDuringExecution,
                                customFunction: data.customFunction
                            }
                        };
                    }
                    return node;
                })
            )
        );
        // Update nodeFormData to reflect saved transitions
        setNodeFormData({
            ...nodeFormData,
            transitions: data.transitions || []
        });
        setShowCustomFunctionModal(false);
        setSelectedNode(null);
        setCustomFunctionData(null);
    };

    const addTransition = () => {
        const newTransition = {
            id: `trans-${Date.now()}`,
            condition: '',
            type: 'prompt',
            target_node_id: ''
        };
        setNodeFormData({
            ...nodeFormData,
            transitions: [...nodeFormData.transitions, newTransition]
        });
        setEditingTransition(newTransition.id);
    };

    const updateTransition = (transitionId, updates) => {
        setNodeFormData({
            ...nodeFormData,
            transitions: nodeFormData.transitions.map((t) =>
                t.id === transitionId ? { ...t, ...updates } : t
            )
        });
    };

    const deleteTransition = (transitionId) => {
        setNodeFormData({
            ...nodeFormData,
            transitions: nodeFormData.transitions.filter((t) => t.id !== transitionId)
        });
    };

    const addVariableToExtract = () => {
        const varName = prompt('Enter variable name (e.g., first_name):');
        if (varName) {
            setNodeFormData({
                ...nodeFormData,
                variables_to_extract: [...nodeFormData.variables_to_extract, varName]
            });
        }
    };

    const removeVariableToExtract = (varName) => {
        setNodeFormData({
            ...nodeFormData,
            variables_to_extract: nodeFormData.variables_to_extract.filter((v) => v !== varName)
        });
    };

    return (
        <div className="w-full h-full relative flex">
            {/* Left Sidebar with Node Types */}
            <div className="w-48 bg-card border-r border-border flex flex-col p-3 gap-2 z-20">
                <div className="text-foreground text-xs font-semibold uppercase tracking-wider mb-2 px-2">
                    Node Types
                </div>
                
                {/* Conversation Node */}
                <button
                    type="button"
                    onClick={() => addNode('custom')}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors group"
                >
                    <div className="w-8 h-8 bg-primary/20 rounded flex items-center justify-center flex-shrink-0">
                        <Hash size={18} className="text-primary" />
                    </div>
                    <span className="text-foreground text-sm font-medium">Conversation</span>
                </button>

                {/* Function Node */}
                <button
                    type="button"
                    onClick={() => addNode('customFunctionNode')}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors group"
                >
                    <div className="w-8 h-8 bg-primary/20 rounded flex items-center justify-center flex-shrink-0">
                        <Cpu size={18} className="text-primary" />
                    </div>
                    <span className="text-foreground text-sm font-medium">Function</span>
                </button>

                {/* Logic Split Node */}
                <button
                    type="button"
                    onClick={() => addNode('logicSplitNode')}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors group"
                >
                    <div className="w-8 h-8 bg-primary/20 rounded flex items-center justify-center flex-shrink-0">
                        <GitBranch size={18} className="text-primary" />
                    </div>
                    <span className="text-foreground text-sm font-medium">Logic Split</span>
                </button>

                {/* Transfer Call Node */}
                <button
                    type="button"
                    onClick={() => addNode('transferCallNode')}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors group"
                >
                    <div className="w-8 h-8 bg-primary/20 rounded flex items-center justify-center flex-shrink-0">
                        <PhoneForwarded size={18} className="text-primary" />
                    </div>
                    <span className="text-foreground text-sm font-medium">Transfer Call</span>
                </button>

                {/* Extract Variable Node - Placeholder for future implementation */}
                {/* <button
                    onClick={() => {}}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors group opacity-50 cursor-not-allowed"
                    title="Coming soon"
                >
                    <div className="w-8 h-8 bg-muted rounded flex items-center justify-center flex-shrink-0">
                        <Braces size={18} className="text-muted-foreground" />
                    </div>
                    <span className="text-muted-foreground text-sm font-medium">Extract Variable</span>
                </button> */}

                {/* Ending Node */}
                <button
                    type="button"
                    onClick={() => addNode('endNode')}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors group"
                >
                    <div className="w-8 h-8 bg-destructive/20 rounded flex items-center justify-center flex-shrink-0">
                        <Square size={18} className="text-destructive" />
                    </div>
                    <span className="text-foreground text-sm font-medium">Ending</span>
                </button>

                <div className="border-t border-border my-2 pt-2">
                    <div className="text-foreground text-xs font-semibold uppercase tracking-wider mb-2 px-2">
                        Tools
                    </div>
                    <button
                        type="button"
                        onClick={autoLayoutFlow}
                        disabled={nodes.length === 0}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors w-full disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Automatically organize nodes in a clean layout"
                    >
                        <LayoutDashboard size={18} className="text-primary flex-shrink-0" />
                        <span className="text-foreground text-sm font-medium">Clean Flow</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowClearConfirm(true)}
                        disabled={nodes.length === 0}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-destructive/10 transition-colors w-full disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Delete every node and clear the entire flow"
                    >
                        <Trash2 size={18} className="text-destructive flex-shrink-0" />
                        <span className="text-destructive text-sm font-medium">Clear Flow</span>
                    </button>
                </div>

                <div className="border-t border-border my-2 pt-2 mt-auto">
                    <div className="text-foreground text-xs font-semibold uppercase tracking-wider mb-2 px-2">
                        JSON
                    </div>
                    <button
                        type="button"
                        onClick={() => { setShowImportModal(true); setImportError(''); setImportJsonText(''); }}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors w-full"
                        title="Paste flow JSON to load"
                    >
                        <ClipboardPaste size={18} className="text-primary flex-shrink-0" />
                        <span className="text-foreground text-sm font-medium">Import JSON</span>
                    </button>
                    <button
                        type="button"
                        onClick={handleExportJson}
                        disabled={nodes.length === 0}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors w-full disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Copy flow as JSON"
                    >
                        <Copy size={18} className="text-primary flex-shrink-0" />
                        <span className="text-foreground text-sm font-medium">{exportCopied ? 'Copied!' : 'Export JSON'}</span>
                    </button>
                </div>
            </div>

            {/* Main Flow Area */}
            <div className="flex-1 relative flow-editor-controls" ref={flowWrapperRef}>

            <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onInit={(instance) => { reactFlowRef.current = instance; }}
                onNodeClick={(event, node) => {
                    // Check node type and open appropriate modal
                    if (node.type === 'logicSplitNode') {
                        openLogicSplitModal(node);
                    } else if (node.type === 'transferCallNode') {
                        openTransferModal(node);
                    } else if (node.type === 'customFunctionNode' || (node.data && node.data.customFunction)) {
                        openCustomFunctionModal(node);
                    } else {
                        openNodeModal(node);
                    }
                }}
                onEdgeClick={(event, edge) => {
                    // Select edge and prevent canvas pan - edge selection is handled by ReactFlow
                    event.stopPropagation();
                }}
                onSelectionChange={handleSelectionChange}
                fitView
                fitViewOptions={{ padding: 0.25, minZoom: 0.45, maxZoom: 1.2 }}
                minZoom={0.35}
                maxZoom={4}
                zoomOnScroll={true}
                zoomOnPinch={true}
                zoomOnDoubleClick={true}
                panOnScroll={false}
                selectionOnDrag={false}
                edgesSelectable={true}
                connectionLineType="smoothstep"
                connectionLineStyle={{ stroke: 'hsl(var(--primary))', strokeWidth: 3 }}
                defaultEdgeOptions={{ type: 'default', style: { stroke: 'hsl(var(--primary))', strokeWidth: 3 } }}
            >
                <Background gap={24} size={1} />
                <Controls className="!bg-card !border !border-border !rounded-lg !shadow-lg [&>button]:!bg-background [&>button:hover]:!bg-muted" />
                <MiniMap nodeStrokeWidth={3} className="!bg-card !border !border-border" />
            </ReactFlow>

            {showNodeModal && (
                <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-card border border-border rounded-lg shadow-lg shadow-primary/20 p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-foreground">Edit Node</h2>
                            <button type="button" onClick={() => setShowNodeModal(false)} className="text-muted-foreground hover:text-foreground transition-colors">
                                <X size={24} />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1 text-foreground">Node Name</label>
                                <input
                                    type="text"
                                    value={nodeFormData.name}
                                    onChange={(e) =>
                                        setNodeFormData({ ...nodeFormData, name: e.target.value })
                                    }
                                    className="w-full bg-background border border-border rounded px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    <input
                                        type="checkbox"
                                        checked={nodeFormData.isEndNode}
                                        onChange={(e) => {
                                            const isEndNode = e.target.checked;
                                            // Only clear transitions if it's an end node AND not a global node
                                            // Global nodes need transitions to define trigger conditions
                                            const shouldClearTransitions = isEndNode && !nodeFormData.isGlobalNode;
                                            setNodeFormData({ 
                                                ...nodeFormData, 
                                                isEndNode,
                                                transitions: shouldClearTransitions ? [] : nodeFormData.transitions
                                            });
                                        }}
                                        className="mr-2"
                                    />
                                    End Node (will end the call after speaking)
                                </label>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    <input
                                        type="checkbox"
                                        checked={nodeFormData.isGlobalNode}
                                        onChange={(e) => {
                                            const isGlobalNode = e.target.checked;
                                            // If unchecking global node and it's an end node, clear transitions
                                            // If checking global node, keep transitions (they define trigger conditions)
                                            const shouldClearTransitions = !isGlobalNode && nodeFormData.isEndNode;
                                            setNodeFormData({ 
                                                ...nodeFormData, 
                                                isGlobalNode,
                                                transitions: shouldClearTransitions ? [] : nodeFormData.transitions
                                            });
                                        }}
                                        className="mr-2"
                                    />
                                    Global Node (can be triggered from anywhere in the conversation)
                                </label>
                                {nodeFormData.isGlobalNode && (
                                    <p className="text-xs text-muted-foreground mt-1 ml-6">
                                        Global nodes need transitions below to define when they trigger. Add a transition with a condition like "user wants to end conversation" or "user says goodbye". A transition with no target node is the trigger; transitions with a target node are where the call goes after this node speaks. A matched global node takes priority over the current node's own transitions.
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    <input
                                        type="checkbox"
                                        checked={nodeFormData.skipResponse}
                                        onChange={(e) =>
                                            setNodeFormData({ ...nodeFormData, skipResponse: e.target.checked })
                                        }
                                        className="mr-2"
                                    />
                                    Skip Response (automatically transition without waiting for user input)
                                </label>
                                {nodeFormData.skipResponse && (
                                    <p className="text-xs text-muted-foreground mt-1 ml-6">
                                        The agent will speak this node's prompt and immediately transition to the next node based on transitions, without waiting for user response.
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2">Agent Response</label>
                                <div className="mb-2 flex gap-2 border-b border-border">
                                    <button
                                        type="button"
                                        onClick={() => setNodeFormData({ ...nodeFormData, prompt_type: 'static_sentence' })}
                                        className={`px-4 py-2 text-sm font-medium transition-colors ${
                                            nodeFormData.prompt_type === 'static_sentence'
                                                ? 'border-b-2 border-primary text-primary'
                                                : 'text-muted-foreground hover:text-foreground'
                                        }`}
                                    >
                                        Static Sentence
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setNodeFormData({ ...nodeFormData, prompt_type: 'prompt' })}
                                        className={`px-4 py-2 text-sm font-medium transition-colors ${
                                            nodeFormData.prompt_type === 'prompt'
                                                ? 'border-b-2 border-primary text-primary'
                                                : 'text-muted-foreground hover:text-foreground'
                                        }`}
                                    >
                                        Prompt
                                    </button>
                                </div>
                                <textarea
                                    value={nodeFormData.prompt}
                                    onChange={(e) =>
                                        setNodeFormData({ ...nodeFormData, prompt: e.target.value })
                                    }
                                    className="w-full bg-background border border-border rounded px-3 py-2 h-32 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                    placeholder={
                                        nodeFormData.prompt_type === 'static_sentence'
                                            ? (nodeFormData.isEndNode ? "Enter the exact sentence the agent should say to end the conversation..." : "Enter the exact sentence the agent should say at this node...")
                                            : (nodeFormData.isEndNode ? "Enter instructions for how the agent should end the conversation..." : "Enter instructions for what the agent should do at this node...")
                                    }
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                    {nodeFormData.prompt_type === 'static_sentence'
                                        ? "The agent will say this exact text word-for-word."
                                        : "The agent will interpret this as instructions and respond semantically."}
                                </p>
                            </div>

                            {(!nodeFormData.isEndNode || nodeFormData.isGlobalNode) && (
                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <label className="block text-sm font-medium">
                                        Transitions
                                        {nodeFormData.isGlobalNode && (
                                            <span className="text-xs text-muted-foreground ml-2">(define when this global node triggers)</span>
                                        )}
                                    </label>
                                    <button
                                        type="button"
                                        onClick={addTransition}
                                        className="text-primary hover:text-primary/80 text-sm flex items-center gap-1 transition-colors"
                                    >
                                        <Plus size={14} />
                                        Add Transition
                                    </button>
                                </div>

                                <div className="space-y-2">
                                    {nodeFormData.transitions.map((transition) => (
                                        <div
                                            key={transition.id}
                                            className="border border-border rounded p-3 space-y-2 bg-muted/20"
                                        >
                                            {editingTransition === transition.id ? (
                                                <>
                                                    <div>
                                                        <label className="block text-xs font-medium mb-1">
                                                            Type
                                                        </label>
                                                        <select
                                                            value={transition.type}
                                                            onChange={(e) => {
                                                                const newType = e.target.value;
                                                                const updates = { type: newType };
                                                                
                                                                // Initialize equation object if switching to equation type
                                                                if (newType === 'equation' && !transition.equation) {
                                                                    // Try to parse existing condition, or use defaults
                                                                    const parsed = parseConditionToEquation(transition.condition);
                                                                    updates.equation = parsed;
                                                                    updates.condition = generateConditionFromEquation(parsed);
                                                                } else if (newType !== 'equation' && transition.equation) {
                                                                    // Clear equation when switching away
                                                                    updates.equation = undefined;
                                                                }
                                                                
                                                                updateTransition(transition.id, updates);
                                                            }}
                                                            className="w-full bg-background border border-border rounded px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                                        >
                                                            <option value="prompt">Prompt</option>
                                                            <option value="equation">Equation</option>
                                                            <option value="status_code">Status Code</option>
                                                        </select>
                                                    </div>
                                                    {transition.type === 'equation' ? (
                                                        <div className="space-y-2">
                                                            <label className="block text-xs font-medium mb-1">
                                                                Conditions
                                                                {nodeFormData.isGlobalNode && (
                                                                    <span className="text-xs text-muted-foreground ml-1">(when this global node triggers)</span>
                                                                )}
                                                            </label>
                                                            <div className="space-y-2">
                                                                {/* Display conditions list */}
                                                                {(transition.equations && transition.equations.length > 0) ? (
                                                                    <div className="space-y-1">
                                                                        {transition.equations.map((eq, idx) => (
                                                                            <div key={idx} className="flex items-center justify-between bg-muted/50 p-2 rounded text-sm text-foreground">
                                                                                <span>{generateConditionFromEquation(eq)}</span>
                                                                                <div className="flex gap-1">
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => {
                                                                                            const newEquations = [...(transition.equations || [])];
                                                                                            newEquations[idx] = { ...eq };
                                                                                            updateTransition(transition.id, {
                                                                                                equations: newEquations,
                                                                                                condition: generateConditionFromEquation(newEquations)
                                                                                            });
                                                                                            setConfiguringConditions(transition.id);
                                                                                        }}
                                                                                        className="text-primary hover:text-primary/80 transition-colors"
                                                                                        title="Edit"
                                                                                    >
                                                                                        <Edit2 size={12} />
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => {
                                                                                            const newEquations = [...(transition.equations || [])];
                                                                                            newEquations.splice(idx, 1);
                                                                                            updateTransition(transition.id, {
                                                                                                equations: newEquations,
                                                                                                condition: generateConditionFromEquation(newEquations),
                                                                                                equationMatchType: transition.equationMatchType || 'any'
                                                                                            });
                                                                                        }}
                                                                                        className="text-destructive hover:text-destructive/80 transition-colors"
                                                                                        title="Delete"
                                                                                    >
                                                                                        <Trash2 size={12} />
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => {
                                                                                            const newEquations = [...(transition.equations || []), { ...eq }];
                                                                                            updateTransition(transition.id, {
                                                                                                equations: newEquations,
                                                                                                condition: generateConditionFromEquation(newEquations),
                                                                                                equationMatchType: transition.equationMatchType || 'any'
                                                                                            });
                                                                                        }}
                                                                                        className="text-muted-foreground hover:text-foreground transition-colors"
                                                                                        title="Duplicate"
                                                                                    >
                                                                                        <Link2 size={12} />
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                ) : (
                                                                    <div className="text-xs text-muted-foreground italic">No conditions added</div>
                                                                )}
                                                                
                                                                {/* Configure conditions button */}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setConfiguringConditions(transition.id)}
                                                                    className="w-full text-sm bg-primary/10 text-primary px-3 py-2 rounded border border-primary/30 hover:bg-primary/20 flex items-center justify-center gap-2 transition-colors"
                                                                >
                                                                    <Plus size={14} />
                                                                    {transition.equations && transition.equations.length > 0 ? 'Configure Conditions' : 'Add Condition'}
                                                                </button>
                                                            </div>
                                                            
                                                            {/* Configure conditions modal/popup */}
                                                            {configuringConditions === transition.id && (
                                                                <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
                                                                    <div className="bg-card border border-border rounded-lg shadow-lg shadow-primary/20 p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
                                                                        <div className="flex justify-between items-center mb-4">
                                                                            <div className="flex items-center gap-2">
                                                                                <h3 className="text-lg font-semibold text-foreground">Configure conditions</h3>
                                                                                <span className="text-muted-foreground" title="Configure multiple conditions for this transition">ⓘ</span>
                                                                            </div>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => setConfiguringConditions(null)}
                                                                                className="text-muted-foreground hover:text-foreground transition-colors"
                                                                            >
                                                                                <X size={20} />
                                                                            </button>
                                                                        </div>
                                                                        
                                                                        <div className="space-y-4">
                                                                            {/* ALL/ANY selector */}
                                                                            <div>
                                                                                <label className="block text-sm font-medium mb-2">
                                                                                    Match Type
                                                                                </label>
                                                                                <select
                                                                                    value={transition.equationMatchType || 'any'}
                                                                                    onChange={(e) => {
                                                                                        updateTransition(transition.id, {
                                                                                            equationMatchType: e.target.value
                                                                                        });
                                                                                    }}
                                                                                    className="w-full border rounded px-3 py-2 text-sm"
                                                                                >
                                                                                    <option value="any">Any of the following conditions match</option>
                                                                                    <option value="all">All of the following conditions match</option>
                                                                                </select>
                                                                            </div>
                                                                            
                                                                            {/* Conditions list */}
                                                                            <div className="space-y-2">
                                                                                <label className="block text-sm font-medium">Conditions</label>
                                                                                {(transition.equations && transition.equations.length > 0) ? (
                                                                                    <div className="space-y-2">
                                                                                        {transition.equations.map((eq, idx) => (
                                                                                            <div key={idx} className="border border-border rounded p-3 space-y-2 bg-muted/50">
                                                                                                <div className="flex gap-2 items-center">
                                                                                                    <div className="flex-1 flex flex-col min-w-0">
                                                                                                        <input
                                                                                                            type="text"
                                                                                                            list={availableVariables.length > 0 ? `equation-var-${transition.id}-${idx}` : undefined}
                                                                                                            value={eq.variable ? `{{${eq.variable}}}` : ''}
                                                                                                            onChange={(e) => {
                                                                                                                const parsed = parseVariableFromInput(e.target.value);
                                                                                                                const newEquations = [...(transition.equations || [])];
                                                                                                                newEquations[idx] = { ...eq, variable: parsed };
                                                                                                                updateTransition(transition.id, {
                                                                                                                    equations: newEquations,
                                                                                                                    condition: generateConditionFromEquation(newEquations)
                                                                                                                });
                                                                                                            }}
                                                                                                            placeholder="e.g. {{program}} or program"
                                                                                                            className="w-full bg-background border border-border rounded px-2 py-1 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                                                                                        />
                                                                                                        {availableVariables.length > 0 && (
                                                                                                            <datalist id={`equation-var-${transition.id}-${idx}`}>
                                                                                                                {availableVariables.map(varName => (
                                                                                                                    <option key={varName} value={`{{${varName}}}`} />
                                                                                                                ))}
                                                                                                            </datalist>
                                                                                                        )}
                                                                                                    </div>
                                                                                                    <select
                                                                                                        value={eq.operator || '='}
                                                                                                        onChange={(e) => {
                                                                                                            const newEquations = [...(transition.equations || [])];
                                                                                                            newEquations[idx] = { ...eq, operator: e.target.value };
                                                                                                            updateTransition(transition.id, {
                                                                                                                equations: newEquations,
                                                                                                                condition: generateConditionFromEquation(newEquations)
                                                                                                            });
                                                                                                        }}
                                                                                                        className="bg-background border border-border rounded px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                                                                                    >
                                                                                                        <option value="=">Equals (=)</option>
                                                                                                        <option value="!=">Does Not Equal (≠)</option>
                                                                                                        <option value=">">Greater Than (&gt;)</option>
                                                                                                        <option value="<">Smaller Than (&lt;)</option>
                                                                                                        <option value=">=">Greater Than or Equal (≥)</option>
                                                                                                        <option value="<=">Smaller Than or Equal (≤)</option>
                                                                                                        <option value="contains">Contains (∈)</option>
                                                                                                        <option value="not contains">Does Not Contain</option>
                                                                                                        <option value="exists">Exists (∃)</option>
                                                                                                        <option value="not exists">Does Not Exist</option>
                                                                                                    </select>
                                                                                                </div>
                                                                                                {(eq.operator !== 'exists' && eq.operator !== 'not exists') && (
                                                                                                    <input
                                                                                                        type="text"
                                                                                                        value={eq.value || ''}
                                                                                                        onChange={(e) => {
                                                                                                            const newEquations = [...(transition.equations || [])];
                                                                                                            newEquations[idx] = { ...eq, value: e.target.value };
                                                                                                            updateTransition(transition.id, {
                                                                                                                equations: newEquations,
                                                                                                                condition: generateConditionFromEquation(newEquations)
                                                                                                            });
                                                                                                        }}
                                                                                                        placeholder="Value"
                                                                                                        className="w-full bg-background border border-border rounded px-2 py-1 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                                                                                    />
                                                                                                )}
                                                                                                <button
                                                                                                    type="button"
                                                                                                    onClick={() => {
                                                                                                        const newEquations = [...(transition.equations || [])];
                                                                                                        newEquations.splice(idx, 1);
                                                                                                        updateTransition(transition.id, {
                                                                                                            equations: newEquations,
                                                                                                            condition: generateConditionFromEquation(newEquations),
                                                                                                            equationMatchType: transition.equationMatchType || 'any'
                                                                                                        });
                                                                                                    }}
                                                                                                    className="text-sm text-destructive hover:text-destructive/80 transition-colors"
                                                                                                >
                                                                                                    Remove
                                                                                                </button>
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                ) : (
                                                                                    <div className="text-xs text-muted-foreground italic">No conditions added yet</div>
                                                                                )}
                                                                                
                                                                                {/* Add condition button */}
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => {
                                                                                        const newEquations = [...(transition.equations || []), { variable: '', operator: '=', value: '' }];
                                                                                        updateTransition(transition.id, {
                                                                                            equations: newEquations,
                                                                                            equationMatchType: transition.equationMatchType || 'any'
                                                                                        });
                                                                                    }}
                                                                                    className="w-full text-sm bg-primary/10 text-primary px-3 py-2 rounded border border-primary/30 hover:bg-primary/20 flex items-center justify-center gap-2 transition-colors"
                                                                                >
                                                                                    <Plus size={14} />
                                                                                    Add Condition
                                                                                </button>
                                                                            </div>
                                                                            
                                                                            <div className="flex justify-end">
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => setConfiguringConditions(null)}
                                                                                    className="px-4 py-2 bg-muted text-foreground rounded hover:bg-muted/80 text-sm transition-colors"
                                                                                >
                                                                                    Done
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div>
                                                            <label className="block text-xs font-medium mb-1">
                                                                Condition
                                                                {nodeFormData.isGlobalNode && (
                                                                    <span className="text-xs text-muted-foreground ml-1">(when this global node triggers)</span>
                                                                )}
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={transition.condition}
                                                                onChange={(e) =>
                                                                    updateTransition(transition.id, {
                                                                        condition: e.target.value
                                                                    })
                                                                }
                                                                className="w-full bg-background border border-border rounded px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                                                placeholder={
                                                                    transition.type === 'prompt' 
                                                                        ? (nodeFormData.isGlobalNode 
                                                                            ? "User wants to end conversation or user says goodbye" 
                                                                            : "User says they are interested in another program")
                                                                        : "Enter condition"
                                                                }
                                                            />
                                                        </div>
                                                    )}
                                                    {!nodeFormData.isEndNode && (
                                                        <div>
                                                            <label className="block text-xs font-medium mb-1">
                                                                Target Node
                                                                {nodeFormData.isGlobalNode && (
                                                                    <span className="text-xs text-muted-foreground ml-1">(optional - where to go after this node)</span>
                                                                )}
                                                            </label>
                                                            <select
                                                                value={transition.target_node_id}
                                                                onChange={(e) =>
                                                                    updateTransition(transition.id, {
                                                                        target_node_id: e.target.value
                                                                    })
                                                                }
                                                                className="w-full bg-background border border-border rounded px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                                            >
                                                                <option value="">Select node...</option>
                                                                {nodes
                                                                    .filter((n) => n.id !== selectedNode?.id)
                                                                    .map((n) => (
                                                                        <option key={n.id} value={n.id}>
                                                                            {n.data.name || n.data.label}
                                                                            {n.data.isEndNode ? ' (End Node)' : ''}
                                                                        </option>
                                                                    ))}
                                                            </select>
                                                        </div>
                                                    )}
                                                    {nodeFormData.isEndNode && nodeFormData.isGlobalNode && (
                                                        <div className="text-xs text-muted-foreground italic">
                                                            Note: Target Node is not needed for end nodes - the call will end after this node executes.
                                                        </div>
                                                    )}
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => setEditingTransition(null)}
                                                            className="text-sm bg-muted px-2 py-1 rounded text-foreground"
                                                        >
                                                            Done
                                                        </button>
                                                        <button
                                                            onClick={() => deleteTransition(transition.id)}
                                                            className="text-sm bg-red-200 text-red-700 px-2 py-1 rounded"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="flex justify-between items-center">
                                                    <div>
                                                        <div className="text-sm font-medium">
                                                            {transition.type === 'equation' && transition.equations && transition.equations.length > 0
                                                                ? (() => {
                                                                    const matchType = transition.equationMatchType === 'all' ? 'ALL' : 'ANY';
                                                                    const conditions = transition.equations.map(eq => generateConditionFromEquation(eq)).filter(s => s).join(', ');
                                                                    return `${matchType}: ${conditions}`;
                                                                })()
                                                                : transition.type === 'equation' && transition.equation
                                                                ? generateConditionFromEquation(transition.equation)
                                                                : transition.condition || 'No condition'}
                                                        </div>
                                                        <div className="text-xs text-muted-foreground">
                                                            {(() => {
                                                                // Map old types to new types for display and capitalize
                                                                let displayType = transition.type === 'semantic' ? 'prompt' : 
                                                                                 transition.type === 'keyword' ? 'prompt' : 
                                                                                 transition.type;
                                                                // Capitalize first letter
                                                                displayType = displayType.charAt(0).toUpperCase() + displayType.slice(1);
                                                                return displayType;
                                                            })()} →{' '}
                                                            {(() => {
                                                                const targetNode = nodes.find((n) => n.id === transition.target_node_id);
                                                                return targetNode ? (targetNode.data?.name || targetNode.data?.label || targetNode.id) : 'No target';
                                                            })()}
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => setEditingTransition(transition.id)}
                                                        className="text-primary hover:text-primary/80 transition-colors"
                                                    >
                                                        <Edit2 size={14} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            )}

                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <label className="block text-sm font-medium">
                                        Variables to Extract
                                    </label>
                                    <button
                                        type="button"
                                        onClick={addVariableToExtract}
                                        className="text-primary hover:text-primary/80 text-sm flex items-center gap-1 transition-colors"
                                    >
                                        <Plus size={14} />
                                        Add Variable
                                    </button>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {nodeFormData.variables_to_extract.map((varName) => (
                                        <span
                                            key={varName}
                                            className="bg-primary/20 text-primary px-2 py-1 rounded text-sm flex items-center gap-1"
                                        >
                                            {varName}
                                            <button
                                                type="button"
                                                onClick={() => removeVariableToExtract(varName)}
                                                className="text-primary hover:text-primary/80 transition-colors"
                                            >
                                                <X size={12} />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            </div>

                            <div className="flex gap-2 justify-end">
                                <button
                                    type="button"
                                    onClick={() => setShowNodeModal(false)}
                                    className="px-4 py-2 border rounded"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={saveNode}
                                    className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20"
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showLogicSplitModal && (
                <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-card border border-border rounded-lg shadow-lg shadow-primary/20 p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">Edit Logic Split Node</h2>
                            <button type="button" onClick={() => setShowLogicSplitModal(false)}>
                                <X size={24} />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1 text-foreground">Node Name</label>
                                <input
                                    type="text"
                                    value={logicSplitFormData.name}
                                    onChange={(e) => setLogicSplitFormData({ ...logicSplitFormData, name: e.target.value })}
                                    placeholder="e.g. Check eligibility"
                                    className="w-full bg-background border border-border rounded px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                />
                            </div>

                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <label className="block text-sm font-medium">Transitions</label>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            addLogicSplitTransition();
                                        }}
                                        className="text-primary hover:text-primary/80 text-sm flex items-center gap-1 transition-colors"
                                    >
                                        <Plus size={14} />
                                        Add Transition
                                    </button>
                                </div>

                                <div className="space-y-2">
                                    {logicSplitFormData.transitions.filter(t => !t.isElse).map((transition) => (
                                        <div
                                            key={transition.id}
                                            className="border border-border rounded p-3 space-y-2 bg-muted/20"
                                        >
                                            {editingTransition === transition.id ? (
                                                <>
                                                    <div>
                                                        <label className="block text-xs font-medium mb-1">Type</label>
                                                        <select
                                                            value={transition.type}
                                                            onChange={(e) => {
                                                                const newType = e.target.value;
                                                                const updates = { type: newType };
                                                                
                                                                // Initialize equations array if switching to equation type
                                                                if (newType === 'equation') {
                                                                    const migrated = migrateTransitionEquation(transition);
                                                                    if (!migrated.equations || migrated.equations.length === 0) {
                                                                        // Try to parse existing condition, or use defaults
                                                                        const parsed = parseConditionToEquation(transition.condition);
                                                                        updates.equations = parsed.variable ? [parsed] : [];
                                                                        updates.equationMatchType = 'any';
                                                                        updates.condition = parsed.variable ? generateConditionFromEquation([parsed]) : '';
                                                                    } else {
                                                                        updates.equations = migrated.equations;
                                                                        updates.equationMatchType = migrated.equationMatchType || 'any';
                                                                        updates.condition = generateConditionFromEquation(migrated.equations);
                                                                    }
                                                                } else if (newType !== 'equation') {
                                                                    // Clear equations when switching away
                                                                    updates.equations = undefined;
                                                                    updates.equationMatchType = undefined;
                                                                }
                                                                
                                                                updateLogicSplitTransition(transition.id, updates);
                                                            }}
                                                            className="w-full bg-background border border-border rounded px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                                        >
                                                            <option value="prompt">Prompt</option>
                                                            <option value="equation">Equation</option>
                                                            <option value="status_code">Status Code</option>
                                                        </select>
                                                    </div>
                                                    {transition.type === 'equation' ? (
                                                        <div className="space-y-2">
                                                            <label className="block text-xs font-medium mb-1">Conditions</label>
                                                            <div className="space-y-2">
                                                                {/* Display conditions list */}
                                                                {(transition.equations && transition.equations.length > 0) ? (
                                                                    <div className="space-y-1">
                                                                        {transition.equations.map((eq, idx) => (
                                                                            <div key={idx} className="flex items-center justify-between bg-muted/50 p-2 rounded text-sm text-foreground">
                                                                                <span>{generateConditionFromEquation(eq)}</span>
                                                                                <div className="flex gap-1">
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => {
                                                                                            const newEquations = [...(transition.equations || [])];
                                                                                            newEquations[idx] = { ...eq };
                                                                                            updateLogicSplitTransition(transition.id, {
                                                                                                equations: newEquations,
                                                                                                condition: generateConditionFromEquation(newEquations)
                                                                                            });
                                                                                            setConfiguringConditions(transition.id);
                                                                                        }}
                                                                                        className="text-primary hover:text-primary/80 transition-colors"
                                                                                        title="Edit"
                                                                                    >
                                                                                        <Edit2 size={12} />
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => {
                                                                                            const newEquations = [...(transition.equations || [])];
                                                                                            newEquations.splice(idx, 1);
                                                                                            updateLogicSplitTransition(transition.id, {
                                                                                                equations: newEquations,
                                                                                                condition: generateConditionFromEquation(newEquations),
                                                                                                equationMatchType: transition.equationMatchType || 'any'
                                                                                            });
                                                                                        }}
                                                                                        className="text-destructive hover:text-destructive/80 transition-colors"
                                                                                        title="Delete"
                                                                                    >
                                                                                        <Trash2 size={12} />
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => {
                                                                                            const newEquations = [...(transition.equations || []), { ...eq }];
                                                                                            updateLogicSplitTransition(transition.id, {
                                                                                                equations: newEquations,
                                                                                                condition: generateConditionFromEquation(newEquations),
                                                                                                equationMatchType: transition.equationMatchType || 'any'
                                                                                            });
                                                                                        }}
                                                                                        className="text-muted-foreground hover:text-foreground transition-colors"
                                                                                        title="Duplicate"
                                                                                    >
                                                                                        <Link2 size={12} />
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                ) : (
                                                                    <div className="text-xs text-muted-foreground italic">No conditions added</div>
                                                                )}
                                                                
                                                                {/* Configure conditions button */}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setConfiguringConditions(transition.id)}
                                                                    className="w-full text-sm bg-primary/10 text-primary px-3 py-2 rounded border border-primary/30 hover:bg-primary/20 flex items-center justify-center gap-2 transition-colors"
                                                                >
                                                                    <Plus size={14} />
                                                                    {transition.equations && transition.equations.length > 0 ? 'Configure Conditions' : 'Add Condition'}
                                                                </button>
                                                            </div>
                                                            
                                                            {/* Configure conditions modal/popup */}
                                                            {configuringConditions === transition.id && (
                                                                <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
                                                                    <div className="bg-card border border-border rounded-lg shadow-lg shadow-primary/20 p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
                                                                        <div className="flex justify-between items-center mb-4">
                                                                            <div className="flex items-center gap-2">
                                                                                <h3 className="text-lg font-semibold text-foreground">Configure conditions</h3>
                                                                                <span className="text-muted-foreground" title="Configure multiple conditions for this transition">ⓘ</span>
                                                                            </div>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => setConfiguringConditions(null)}
                                                                                className="text-muted-foreground hover:text-foreground transition-colors"
                                                                            >
                                                                                <X size={20} />
                                                                            </button>
                                                                        </div>
                                                                        
                                                                        <div className="space-y-4">
                                                                            {/* ALL/ANY selector */}
                                                                            <div>
                                                                                <label className="block text-sm font-medium mb-2">
                                                                                    Match Type
                                                                                </label>
                                                                                <select
                                                                                    value={transition.equationMatchType || 'any'}
                                                                                    onChange={(e) => {
                                                                                        updateLogicSplitTransition(transition.id, {
                                                                                            equationMatchType: e.target.value
                                                                                        });
                                                                                    }}
                                                                                    className="w-full border rounded px-3 py-2 text-sm"
                                                                                >
                                                                                    <option value="any">Any of the following conditions match</option>
                                                                                    <option value="all">All of the following conditions match</option>
                                                                                </select>
                                                                            </div>
                                                                            
                                                                            {/* Conditions list */}
                                                                            <div className="space-y-2">
                                                                                <label className="block text-sm font-medium">Conditions</label>
                                                                                {(transition.equations && transition.equations.length > 0) ? (
                                                                                    <div className="space-y-2">
                                                                                        {transition.equations.map((eq, idx) => (
                                                                                            <div key={idx} className="border border-border rounded p-3 space-y-2 bg-muted/50">
                                                                                                <div className="flex gap-2 items-center">
                                                                                                    <div className="flex-1 flex flex-col min-w-0">
                                                                                                        <input
                                                                                                            type="text"
                                                                                                            list={availableVariables.length > 0 ? `equation-var-logic-${transition.id}-${idx}` : undefined}
                                                                                                            value={eq.variable ? `{{${eq.variable}}}` : ''}
                                                                                                            onChange={(e) => {
                                                                                                                const parsed = parseVariableFromInput(e.target.value);
                                                                                                                const newEquations = [...(transition.equations || [])];
                                                                                                                newEquations[idx] = { ...eq, variable: parsed };
                                                                                                                updateLogicSplitTransition(transition.id, {
                                                                                                                    equations: newEquations,
                                                                                                                    condition: generateConditionFromEquation(newEquations)
                                                                                                                });
                                                                                                            }}
                                                                                                            placeholder="e.g. {{program}} or program"
                                                                                                            className="w-full bg-background border border-border rounded px-2 py-1 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                                                                                        />
                                                                                                        {availableVariables.length > 0 && (
                                                                                                            <datalist id={`equation-var-logic-${transition.id}-${idx}`}>
                                                                                                                {availableVariables.map(varName => (
                                                                                                                    <option key={varName} value={`{{${varName}}}`} />
                                                                                                                ))}
                                                                                                            </datalist>
                                                                                                        )}
                                                                                                    </div>
                                                                                                    <select
                                                                                                        value={eq.operator || '='}
                                                                                                        onChange={(e) => {
                                                                                                            const newEquations = [...(transition.equations || [])];
                                                                                                            newEquations[idx] = { ...eq, operator: e.target.value };
                                                                                                            updateLogicSplitTransition(transition.id, {
                                                                                                                equations: newEquations,
                                                                                                                condition: generateConditionFromEquation(newEquations)
                                                                                                            });
                                                                                                        }}
                                                                                                        className="bg-background border border-border rounded px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                                                                                    >
                                                                                                        <option value="=">Equals (=)</option>
                                                                                                        <option value="!=">Does Not Equal (≠)</option>
                                                                                                        <option value=">">Greater Than (&gt;)</option>
                                                                                                        <option value="<">Smaller Than (&lt;)</option>
                                                                                                        <option value=">=">Greater Than or Equal (≥)</option>
                                                                                                        <option value="<=">Smaller Than or Equal (≤)</option>
                                                                                                        <option value="contains">Contains (∈)</option>
                                                                                                        <option value="not contains">Does Not Contain</option>
                                                                                                        <option value="exists">Exists (∃)</option>
                                                                                                        <option value="not exists">Does Not Exist</option>
                                                                                                    </select>
                                                                                                </div>
                                                                                                {(eq.operator !== 'exists' && eq.operator !== 'not exists') && (
                                                                                                    <input
                                                                                                        type="text"
                                                                                                        value={eq.value || ''}
                                                                                                        onChange={(e) => {
                                                                                                            const newEquations = [...(transition.equations || [])];
                                                                                                            newEquations[idx] = { ...eq, value: e.target.value };
                                                                                                            updateLogicSplitTransition(transition.id, {
                                                                                                                equations: newEquations,
                                                                                                                condition: generateConditionFromEquation(newEquations)
                                                                                                            });
                                                                                                        }}
                                                                                                        placeholder="Value"
                                                                                                        className="w-full bg-background border border-border rounded px-2 py-1 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                                                                                    />
                                                                                                )}
                                                                                                <button
                                                                                                    type="button"
                                                                                                    onClick={() => {
                                                                                                        const newEquations = [...(transition.equations || [])];
                                                                                                        newEquations.splice(idx, 1);
                                                                                                        updateLogicSplitTransition(transition.id, {
                                                                                                            equations: newEquations,
                                                                                                            condition: generateConditionFromEquation(newEquations),
                                                                                                            equationMatchType: transition.equationMatchType || 'any'
                                                                                                        });
                                                                                                    }}
                                                                                                    className="text-sm text-destructive hover:text-destructive/80 transition-colors"
                                                                                                >
                                                                                                    Remove
                                                                                                </button>
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                ) : (
                                                                                    <div className="text-xs text-muted-foreground italic">No conditions added yet</div>
                                                                                )}
                                                                                
                                                                                {/* Add condition button */}
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => {
                                                                                        const newEquations = [...(transition.equations || []), { variable: '', operator: '=', value: '' }];
                                                                                        updateLogicSplitTransition(transition.id, {
                                                                                            equations: newEquations,
                                                                                            equationMatchType: transition.equationMatchType || 'any'
                                                                                        });
                                                                                    }}
                                                                                    className="w-full text-sm bg-primary/10 text-primary px-3 py-2 rounded border border-primary/30 hover:bg-primary/20 flex items-center justify-center gap-2 transition-colors"
                                                                                >
                                                                                    <Plus size={14} />
                                                                                    Add Condition
                                                                                </button>
                                                                            </div>
                                                                            
                                                                            <div className="flex justify-end">
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => setConfiguringConditions(null)}
                                                                                    className="px-4 py-2 bg-muted text-foreground rounded hover:bg-muted/80 text-sm transition-colors"
                                                                                >
                                                                                    Done
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div>
                                                            <label className="block text-xs font-medium mb-1">Condition</label>
                                                            <input
                                                                type="text"
                                                                value={transition.condition}
                                                                onChange={(e) =>
                                                                    updateLogicSplitTransition(transition.id, {
                                                                        condition: e.target.value
                                                                    })
                                                                }
                                                                className="w-full bg-background border border-border rounded px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                                                placeholder={transition.type === 'prompt' ? "User says they are interested in another program" : "Enter condition"}
                                                            />
                                                        </div>
                                                    )}
                                                    <div>
                                                        <label className="block text-xs font-medium mb-1">Target Node</label>
                                                        <select
                                                            value={transition.target_node_id}
                                                            onChange={(e) =>
                                                                updateLogicSplitTransition(transition.id, {
                                                                    target_node_id: e.target.value
                                                                })
                                                            }
                                                            className="w-full bg-background border border-border rounded px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                                        >
                                                            <option value="">Select node...</option>
                                                            {nodes
                                                                .filter((n) => n.id !== selectedNode?.id)
                                                                .map((n) => (
                                                                    <option key={n.id} value={n.id}>
                                                                        {n.data.name || n.data.label}
                                                                    </option>
                                                                ))}
                                                        </select>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                setEditingTransition(null);
                                                            }}
                                                            className="text-sm bg-muted px-2 py-1 rounded text-foreground"
                                                        >
                                                            Done
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                deleteLogicSplitTransition(transition.id);
                                                            }}
                                                            className="text-sm bg-red-200 text-red-700 px-2 py-1 rounded"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="flex justify-between items-center">
                                                    <div>
                                                        <div className="text-sm font-medium">
                                                            {transition.type === 'equation' && transition.equations && transition.equations.length > 0
                                                                ? (() => {
                                                                    const matchType = transition.equationMatchType === 'all' ? 'ALL' : 'ANY';
                                                                    const conditions = transition.equations.map(eq => generateConditionFromEquation(eq)).filter(s => s).join(', ');
                                                                    return `${matchType}: ${conditions}`;
                                                                })()
                                                                : transition.type === 'equation' && transition.equation
                                                                ? generateConditionFromEquation(transition.equation)
                                                                : transition.condition || 'No condition'}
                                                        </div>
                                                        <div className="text-xs text-muted-foreground">
                                                            {(() => {
                                                                // Map old types to new types for display and capitalize
                                                                let displayType = transition.type === 'semantic' ? 'prompt' : 
                                                                                 transition.type === 'keyword' ? 'prompt' : 
                                                                                 transition.type;
                                                                // Capitalize first letter
                                                                displayType = displayType.charAt(0).toUpperCase() + displayType.slice(1);
                                                                return displayType;
                                                            })()} →{' '}
                                                            {(() => {
                                                                const targetNode = nodes.find((n) => n.id === transition.target_node_id);
                                                                return targetNode ? (targetNode.data?.name || targetNode.data?.label || targetNode.id) : 'No target';
                                                            })()}
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => setEditingTransition(transition.id)}
                                                        className="text-primary hover:text-primary/80 transition-colors"
                                                    >
                                                        <Edit2 size={14} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Else Transition */}
                            <div className="pt-4 border-t">
                                <div className="flex items-center gap-2 mb-2">
                                    <Sparkles size={16} className="text-primary" />
                                    <label className="block text-sm font-medium">Else</label>
                                </div>
                                {logicSplitFormData.transitions.filter(t => t.isElse).map((elseTransition) => (
                                    <div key={elseTransition.id} className="border border-border rounded p-3 space-y-2 bg-muted/50">
                                        {editingTransition === elseTransition.id ? (
                                            <>
                                                <div>
                                                    <label className="block text-xs font-medium mb-1">Target Node</label>
                                                    <select
                                                        value={elseTransition.target_node_id}
                                                        onChange={(e) =>
                                                            updateLogicSplitTransition(elseTransition.id, {
                                                                target_node_id: e.target.value
                                                            })
                                                        }
                                                        className="w-full bg-background border border-border rounded px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                                    >
                                                        <option value="">Select node...</option>
                                                        {nodes
                                                            .filter((n) => n.id !== selectedNode?.id)
                                                            .map((n) => (
                                                                <option key={n.id} value={n.id}>
                                                                    {n.data.name || n.data.label}
                                                                </option>
                                                            ))}
                                                    </select>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        setEditingTransition(null);
                                                    }}
                                                    className="text-sm bg-muted text-foreground px-2 py-1 rounded hover:bg-muted/80 transition-colors"
                                                >
                                                    Done
                                                </button>
                                            </>
                                        ) : (
                                            <div className="flex justify-between items-center">
                                                <div>
                                                    <div className="text-sm font-medium">Else</div>
                                                    <div className="text-xs text-muted-foreground">
                                                        → {(() => {
                                                            const targetNode = nodes.find((n) => n.id === elseTransition.target_node_id);
                                                            return targetNode ? (targetNode.data?.name || targetNode.data?.label || targetNode.id) : 'No target';
                                                        })()}
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        setEditingTransition(elseTransition.id);
                                                    }}
                                                    className="text-primary hover:text-primary/80 transition-colors"
                                                >
                                                    <Edit2 size={14} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1 text-foreground">
                                    <input
                                        type="checkbox"
                                        checked={logicSplitFormData.isGlobalNode || false}
                                        onChange={(e) => setLogicSplitFormData({
                                            ...logicSplitFormData,
                                            isGlobalNode: e.target.checked,
                                            globalTriggers: e.target.checked && !(logicSplitFormData.globalTriggers || []).length
                                                ? [{ id: `trig-${Date.now()}`, condition: '' }]
                                                : (logicSplitFormData.globalTriggers || []),
                                            error: ''
                                        })}
                                        className="mr-2"
                                    />
                                    Global Node (the caller can reach it from any point in the call)
                                </label>
                                {logicSplitFormData.isGlobalNode && (
                                    <div className="space-y-2 mt-2">
                                        <p className="text-xs text-muted-foreground">The flow jumps to this split whenever the caller's message matches one of these conditions, for example "User asks to speak with a person". The split then checks its transitions above as usual.</p>
                                        {(logicSplitFormData.globalTriggers || []).map((t) => (
                                            <div key={t.id} className="flex gap-2 items-center">
                                                <input
                                                    type="text"
                                                    value={t.condition}
                                                    onChange={(e) => setLogicSplitFormData({
                                                        ...logicSplitFormData,
                                                        globalTriggers: logicSplitFormData.globalTriggers.map(x => x.id === t.id ? { ...x, condition: e.target.value } : x),
                                                        error: ''
                                                    })}
                                                    placeholder="User asks to speak with a person"
                                                    className="w-full bg-background border border-border rounded px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setLogicSplitFormData({ ...logicSplitFormData, globalTriggers: logicSplitFormData.globalTriggers.filter(x => x.id !== t.id) })}
                                                    className="text-destructive hover:text-destructive/80 p-2"
                                                    title="Remove condition"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={() => setLogicSplitFormData({ ...logicSplitFormData, globalTriggers: [...(logicSplitFormData.globalTriggers || []), { id: `trig-${Date.now()}`, condition: '' }] })}
                                            className="text-primary hover:text-primary/80 text-sm flex items-center gap-1 transition-colors"
                                        >
                                            <Plus size={14} />
                                            Add Condition
                                        </button>
                                    </div>
                                )}
                            </div>

                            {logicSplitFormData.error && (
                                <p className="text-sm text-destructive">{logicSplitFormData.error}</p>
                            )}

                            <div className="flex gap-2 justify-end">
                                <button
                                    type="button"
                                    onClick={() => setShowLogicSplitModal(false)}
                                    className="px-4 py-2 border rounded"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={saveLogicSplit}
                                    className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20"
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showTransferModal && transferFormData && (
                <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-card border border-border rounded-lg shadow-lg shadow-primary/20 p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-foreground">Edit Transfer Call Node</h2>
                            <button type="button" onClick={closeTransferModal} className="text-muted-foreground hover:text-foreground transition-colors">
                                <X size={24} />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1 text-foreground">Node Name</label>
                                <input
                                    type="text"
                                    value={transferFormData.name}
                                    onChange={(e) => setTransferFormData({ ...transferFormData, name: e.target.value })}
                                    className="w-full bg-background border border-border rounded px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1 text-foreground">Transfer Type</label>
                                <div className="grid grid-cols-2 gap-2">
                                    {[
                                        { value: 'cold', label: 'Cold transfer', help: 'The caller is connected as soon as the person answers.' },
                                        { value: 'warm', label: 'Warm transfer', help: 'The caller holds while the agent briefs the person, then the agent connects them.' }
                                    ].map(opt => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => setTransferFormData({ ...transferFormData, transfer_type: opt.value })}
                                            className={`text-left rounded border px-3 py-2 transition-colors ${transferFormData.transfer_type === opt.value ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted'}`}
                                        >
                                            <div className="text-sm font-medium text-foreground">{opt.label}</div>
                                            <div className="text-xs text-muted-foreground">{opt.help}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1 text-foreground">Transfer Number</label>
                                <input
                                    type="text"
                                    value={transferFormData.target_number}
                                    onChange={(e) => setTransferFormData({ ...transferFormData, target_number: e.target.value, error: '' })}
                                    placeholder="+15551234567"
                                    className="w-full bg-background border border-border rounded px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-mono"
                                />
                                <p className="text-xs text-muted-foreground mt-1">Use E.164 format. A variable such as {'{{transfer_number}}'} also works.</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1 text-foreground">Transfer Ring Duration (seconds)</label>
                                <input
                                    type="number"
                                    min={TRANSFER_RING_MIN_S}
                                    max={TRANSFER_RING_MAX_S}
                                    value={transferFormData.ring_duration_seconds}
                                    onChange={(e) => setTransferFormData({ ...transferFormData, ring_duration_seconds: e.target.value, error: '' })}
                                    className="w-full bg-background border border-border rounded px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                    How long the number rings before the agent stops trying ({TRANSFER_RING_MIN_S} to {TRANSFER_RING_MAX_S} seconds). If nobody answers, the agent tells the caller and keeps the conversation going.
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1 text-foreground">Message Before Transfer</label>
                                <textarea
                                    value={transferFormData.prompt}
                                    onChange={(e) => setTransferFormData({ ...transferFormData, prompt: e.target.value })}
                                    placeholder={DEFAULT_TRANSFER_MESSAGES[transferFormData.transfer_type]}
                                    rows={2}
                                    className="w-full bg-background border border-border rounded px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                />
                                <p className="text-xs text-muted-foreground mt-1">The agent says this word for word before the transfer starts. Leave it blank to use the default shown.</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1 text-foreground">Message If Nobody Answers</label>
                                <textarea
                                    value={transferFormData.failure_message}
                                    onChange={(e) => setTransferFormData({ ...transferFormData, failure_message: e.target.value })}
                                    placeholder={DEFAULT_TRANSFER_FAILURE_MESSAGE}
                                    rows={3}
                                    className="w-full bg-background border border-border rounded px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                    After this message the call goes to the node chosen below.
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1 text-foreground">If the Transfer Fails, Go To</label>
                                <select
                                    value={transferFormData.failed_target_node_id}
                                    onChange={(e) => setTransferFormData({ ...transferFormData, failed_target_node_id: e.target.value })}
                                    className="w-full bg-background border border-border rounded px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                >
                                    <option value="">Return to the node the caller was on</option>
                                    {nodes
                                        .filter((n) => n.id !== selectedNode?.id)
                                        .map((n) => (
                                            <option key={n.id} value={n.id}>
                                                {n.data.name || n.data.label}
                                                {n.data.isEndNode ? ' (End Node)' : ''}
                                            </option>
                                        ))}
                                </select>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Same as the node's "Transfer failed" connection on the canvas. Changing either one updates the other.
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1 text-foreground">
                                    <input
                                        type="checkbox"
                                        checked={transferFormData.isGlobalNode}
                                        onChange={(e) => setTransferFormData({
                                            ...transferFormData,
                                            isGlobalNode: e.target.checked,
                                            triggers: e.target.checked && transferFormData.triggers.length === 0
                                                ? [{ id: `trans-${Date.now()}`, condition: '' }]
                                                : transferFormData.triggers,
                                            error: ''
                                        })}
                                        className="mr-2"
                                    />
                                    Global Node (the caller can reach it from any point in the call)
                                </label>
                                {transferFormData.isGlobalNode && (
                                    <div className="space-y-2 mt-2">
                                        <p className="text-xs text-muted-foreground">The agent transfers whenever the caller's message matches one of these conditions, for example "User asks to speak with a person".</p>
                                        {transferFormData.triggers.map((t) => (
                                            <div key={t.id} className="flex gap-2 items-center">
                                                <input
                                                    type="text"
                                                    value={t.condition}
                                                    onChange={(e) => setTransferFormData({
                                                        ...transferFormData,
                                                        triggers: transferFormData.triggers.map(x => x.id === t.id ? { ...x, condition: e.target.value } : x),
                                                        error: ''
                                                    })}
                                                    placeholder="User asks to speak with a person"
                                                    className="w-full bg-background border border-border rounded px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setTransferFormData({ ...transferFormData, triggers: transferFormData.triggers.filter(x => x.id !== t.id) })}
                                                    className="text-destructive hover:text-destructive/80 p-2"
                                                    title="Remove condition"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={() => setTransferFormData({ ...transferFormData, triggers: [...transferFormData.triggers, { id: `trans-${Date.now()}`, condition: '' }] })}
                                            className="text-primary hover:text-primary/80 text-sm flex items-center gap-1 transition-colors"
                                        >
                                            <Plus size={14} />
                                            Add Condition
                                        </button>
                                    </div>
                                )}
                            </div>

                            <p className="text-xs text-muted-foreground">Transfers run on phone calls only. On a web test call the agent goes straight to the "nobody answers" path.</p>

                            {transferFormData.error && (
                                <p className="text-sm text-destructive">{transferFormData.error}</p>
                            )}

                            <div className="flex gap-2 justify-end">
                                <button
                                    type="button"
                                    onClick={closeTransferModal}
                                    className="px-4 py-2 border rounded"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={saveTransferNode}
                                    className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20"
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Clear Flow confirmation modal */}
            {showClearConfirm && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-card border border-border rounded-lg shadow-xl max-w-md w-full flex flex-col">
                        <div className="p-4 border-b border-border flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Trash2 size={20} className="text-destructive" />
                                <h3 className="text-lg font-semibold text-foreground">Clear entire flow?</h3>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowClearConfirm(false)}
                                className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-4">
                            <p className="text-sm text-muted-foreground">
                                This deletes <span className="font-semibold text-foreground">every node</span> and connection in this flow
                                {nodes.length > 0 ? ` (${nodes.length} node${nodes.length === 1 ? '' : 's'})` : ''}.
                                This cannot be undone. The deletion is saved when you save the agent.
                            </p>
                        </div>
                        <div className="p-4 border-t border-border flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setShowClearConfirm(false)}
                                className="px-4 py-2 rounded border border-border hover:bg-muted"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleClearFlow}
                                className="px-4 py-2 rounded bg-destructive text-destructive-foreground hover:bg-destructive/90 flex items-center gap-2"
                            >
                                <Trash2 size={16} />
                                Clear Flow
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Import JSON Modal */}
            {showImportModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-card border border-border rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
                        <div className="p-4 border-b border-border flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-foreground">Import flow from JSON</h3>
                            <button
                                type="button"
                                onClick={() => { setShowImportModal(false); setImportJsonText(''); setImportError(''); }}
                                className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-4 flex-1 flex flex-col min-h-0">
                            <p className="text-sm text-muted-foreground mb-2">Paste a flow JSON below (must include a <code className="bg-muted px-1 rounded">nodes</code> array). This will replace the current flow.</p>
                            <textarea
                                value={importJsonText}
                                onChange={(e) => setImportJsonText(e.target.value)}
                                placeholder='{"nodes": [...], "edges": [...], "start_node_id": "node-1"}'
                                className="w-full flex-1 min-h-[200px] p-3 rounded border border-border bg-background text-foreground font-mono text-sm resize-y"
                                spellCheck={false}
                            />
                            {importError && (
                                <p className="mt-2 text-sm text-destructive">{importError}</p>
                            )}
                        </div>
                        <div className="p-4 border-t border-border flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => { setShowImportModal(false); setImportJsonText(''); setImportError(''); }}
                                className="px-4 py-2 rounded border border-border hover:bg-muted"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleImportJson}
                                className="px-4 py-2 rounded bg-primary text-primary-foreground hover:bg-primary/90"
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <CustomFunctionModal
                isOpen={showCustomFunctionModal}
                onClose={() => {
                    setShowCustomFunctionModal(false);
                    setSelectedNode(null);
                    setCustomFunctionData(null);
                }}
                onSave={saveCustomFunction}
                customFunction={customFunctionData}
                nodeData={nodeFormData}
                transitions={nodeFormData.transitions}
                nodes={nodes}
                currentNodeId={selectedNode?.id}
            />
            </div>
            </div>
    );
});

FlowBuilder.displayName = 'FlowBuilder';

export default FlowBuilder;
